import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetFunctionsByEventId, GetGuestsByEventId, GetGuestsByEventIdForMember,
  GetGuestsByEventIdAndMobile, AddGuestToFunctions, AddOrMoveFamilyMember, UpdateGuest,
  SetGuestFunctions, GetTeamByEventId,
} from '../../api/api';
import {
  isMarriageEventType, normaliseText, isPastFunction, tenDigits, nameInput, isWeddingFunction, toggleWithWedding,
  newGuestFunctionIds, openAutoFunctionIds, GUEST_GROUPS, groupKey, isCountGroup, COUNT_GROUP_TYPE, fmtDate, fmtTime, NO_CARD_STATUS,
  guestTypeForGroup, groupForGuestType, RELATION_HEAD, relationLabel, adderInitials, adderShortNames, adderLegend } from '../../utils/helpers';
import { contactsSupported, pickContactFromDevice, bulkContactsSupported, getDeviceContacts } from '../../utils/contacts';
import { shareInvite } from '../../utils/whatsappInvite';
import { buildFunctionSets } from '../../utils/functionSets';
import { parseFamilyNames } from '../../utils/quickEntry';
import { sortFamilyMembers, moveMember } from '../../utils/familyOrder';
import { useQuickAdd } from './useQuickAdd';
import { useCountGroup } from './useCountGroup';
import { usePeopleSearch } from './usePeopleSearch';
import { useContactSuggest } from './useContactSuggest';

export const MAX_FAMILY_SIZE = 9; // 1 family head + up to 8 other members
// A new family starts with this many blank member lines to type into (blank
// lines are ignored on save), so a household is entered without extra clicks.
const FAMILY_START_ROWS = 6;   // the grid is short now - show six waiting lines, blank ones are skipped
const blankMember = () => ({ Surname: '', GuestName: '', MobileNumber: '', Relation: '', FunctionIds: [] });
const blankMembers = () => Array.from({ length: FAMILY_START_ROWS }, blankMember);

// Last-used functions / side / visibility, remembered per event so a member
// adding 50 guests for the same functions ticks them once (localStorage).
const defaultsKey = (eventId) => `se_guest_defaults:${eventId}`;
const readDefaults = (eventId) => { try { return JSON.parse(localStorage.getItem(defaultsKey(eventId))) || {}; } catch { return {}; } };
const writeDefaults = (eventId, d) => { try { localStorage.setItem(defaultsKey(eventId), JSON.stringify(d)); } catch { /* ignore */ } };
export const INVITE_COOLDOWN_SECONDS = 10;
const ten = tenDigits;
const emptyToNull = (obj) => {
  const out = {};
  for (const k of Object.keys(obj)) out[k] = obj[k] === '' ? null : obj[k];
  return out;
};
const blankForm = () => ({
  FunctionId: '', FunctionIds: [], Surname: '', GuestName: '', FatherHusband: '', MobileNumber: '',
  // A business associate is invited as a firm: the card names the owner with
  // the company under it (GuestType is 'Family' for everyone else).
  GuestType: 'Family', CompanyName: '', City: '',
  // The rest of this household, as plain names ("Kamla, Ravi 9822011111") -
  // 83% of guests sit in one, and this saves them on the same card.
  FamilyNames: '',
  Side: 'Common', Relation: '', GroupName: '', EmailId: '', ExpectedCount: 1,
  Visibility: 'Private', InviteStatus: 'Pending', InvitedVia: '', Remarks: '',
  ExtraCards: '', // extra invitation cards the head asked for (names unknown, Reception only)
  // Invitation card (Cards page fields): own card, or on another family head's card.
  CardMode: 'own', CardFamilyId: '', CardHeadText: '', CardName: '', CardDeliveredByName: '',
  // Name on the card: follows the guest's own name until typed over or cleared (×).
  CardNameAuto: true,
});

// Rows of ONE person across several functions collapse into one card. A
// mobile number identifies a person; without one two guests of the same name
// cannot be told apart, so each row stays its own card rather than merging two
// different Rameshes (numbers are optional now).
const guestGroupKey = (row) => {
  const owner = row.CreatedBy || 'admin';
  const mobile = String(row.MobileNumber || '').trim();
  if (mobile) return `${owner}:mobile:${mobile}`;
  return `guest:${row.GuestId}`;
};

const rowFunctionIds = (row) => {
  if (row.FunctionIds) return String(row.FunctionIds).split(',').map(v => v.trim()).filter(Boolean);
  return row.FunctionId ? [String(row.FunctionId)] : [];
};

const combineGuestRows = (list) => {
  const groups = new Map();
  list.forEach(row => {
    const key = guestGroupKey(row);
    if (!groups.has(key)) {
      groups.set(key, {
        ...row,
        _rows: [],
        _guestIds: [],
        _functionIds: [],
        FunctionNames: '',
      });
    }
    const group = groups.get(key);
    group._rows.push(row);
    group._guestIds.push(row.GuestId);
    rowFunctionIds(row).forEach(id => {
      if (!group._functionIds.some(existing => String(existing) === String(id))) group._functionIds.push(id);
    });
    const names = String(row.FunctionNames || row.FunctionName || '').split(',').map(v => v.trim()).filter(Boolean);
    names.forEach(name => {
      if (!group.FunctionNames.split(', ').includes(name)) {
        group.FunctionNames = group.FunctionNames ? `${group.FunctionNames}, ${name}` : name;
      }
    });
    group.ExpectedCount = Math.max(Number(group.ExpectedCount || 1), Number(row.ExpectedCount || 1));
  });
  return Array.from(groups.values());
};

// Sortable columns of the guest list: key → value extractor. Order of
// SORT_OPTIONS is what the "Sort" dropdown shows.
const lc = (v) => (v || '').toString().trim().toLowerCase();
// own = in no family · head = heads a family · member = on someone else's card
const cardRole = (r) => { const f = Number(r.FamilyId || 0); return !f ? 'own' : (f === Number(r.GuestId) ? 'head' : 'member'); };
const sideKey = (s) => (s === 'Male' ? 'Groom' : s === 'Female' ? 'Bride' : (s || 'Common'));
export const SORT_KEYS = {
  name: (r) => lc([r.Surname, r.GuestName, r.FatherHusband].filter(Boolean).join(' ')),
  firstName: (r) => lc(r.GuestName),
  mobile: (r) => lc(r.MobileNumber),
  function: (r) => lc(r.FunctionNames || r.FunctionName),
  side: (r) => lc(r.Side),
  pax: (r) => Number(r.ExpectedCount || 1),
  visibility: (r) => lc(r.Visibility || 'Private'),
  addedBy: (r) => lc(r.AddedByName || r.InvitedByName),
  status: (r) => lc(r.InviteStatus),
  rsvp: (r) => lc(r.RSVPStatus),
  city: (r) => lc(r.City),
  guestType: (r) => lc(r.GuestType || 'Family'),
  added: (r) => lc(r.CreatedDate),
};
export const SORT_OPTIONS = [
  ['name', 'Surname + Name'], ['firstName', 'First name'], ['mobile', 'Mobile'], ['function', 'Function'],
  ['side', 'Side'], ['pax', 'Persons'], ['visibility', 'Visibility'], ['addedBy', 'Added by'],
  ['status', 'Status'], ['rsvp', 'RSVP'], ['city', 'City'], ['added', 'Date added'],
];

// All Members logic — shared by the desktop and mobile UIs.
export function useMembers(event) {
  const { user, organiser, isAdmin, can, mySide, sideLocked } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  const canView = can('CanMembers');
  const canAdd = can('CanMembersAdd') || can('CanMembers');
  const canEdit = can('CanMembersEdit') || can('CanMembers');
  const canDelete = can('CanMembersDelete') || can('CanMembers');
  const mine = (row) => isAdmin || String(row.CreatedBy) === String(me);

  const [functions, setFunctions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  // `?fn=<FunctionId>` (from the Functions / Headcount drill-in) preselects the filter.
  const [searchParams] = useSearchParams();
  // fnFilter: '' all · a FunctionId · '__all' invited to every open function ·
  // '__some' missing at least one open function. fnMode (with a FunctionId):
  // 'in' invited to it · 'out' NOT invited to it.
  const [fnFilter, setFnFilter] = useState(() => searchParams.get('fn') || '');
  const [fnMode, setFnMode] = useState('in');
  const [sideFilter, setSideFilter] = useState('');
  // Card filter: '' all guests · 'cards' own card + family heads (one row per
  // card) · 'own' only guests on their own card (in no family).
  const [cardFilter, setCardFilter] = useState('');
  // Whatever side this user belongs to (My Profile) is the side of the guests
  // they add — the guest screens never ask.
  const guestSide = mySide || 'Common';
  const isMarriage = isMarriageEventType(event?.EventTypeName || event?.EventType);
  // Two-sided event: an ordinary member is pinned to one side (their
  // Evt_UserEventsMapping.Side) and every guest they add gets it — no picker, no
  // dashboard toggle. Admins keep both sides. A member whose side an admin has
  // not assigned yet cannot add guests at all (sideMissing).
  // The side comes from the user (My Profile) for every role: the guest form
  // shows it, never asks. An admin set to 'Both/Common' adds Common guests.
  const lockedSide = isMarriage ? mySide : null;
  const sideMissing = isMarriage && sideLocked && !mySide;
  const sideOptions = isMarriage ? (lockedSide ? [lockedSide] : ['Common', 'Groom', 'Bride']) : ['Common'];
  const defaultSide = isMarriage ? (lockedSide || guestSide) : 'Common';

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => ({ ...blankForm(), Side: defaultSide }));
  const [party, setParty] = useState([]);
  const [familyMode, setFamilyMode] = useState(false);
  const [addMode, setAddMode] = useState('new');
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [checkedMobile, setCheckedMobile] = useState('');
  const [inviteRow, setInviteRow] = useState(null);
  const [inviteSharing, setInviteSharing] = useState('');
  const [inviteCooldown, setInviteCooldown] = useState(0);
  // How many guests were added since the form was opened ("Save & add next").
  const [addedThisSession, setAddedThisSession] = useState(0);
  // The Wedding is ticked with any other function unless it was unticked by
  // hand for this guest (see toggleWithWedding in helpers).
  const [weddingOff, setWeddingOff] = useState(false);
  // Set when Add Guest found the person already in this event and switched to
  // editing them (their name) - the form says so instead of blocking.
  const [existingNotice, setExistingNotice] = useState('');
  // Team members, for "card delivered by" suggestions.
  const [team, setTeam] = useState([]);

  // ── Bulk "import from contacts" state ──────────────────────────────────────
  const canBulkImport = bulkContactsSupported();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [deviceContacts, setDeviceContacts] = useState([]);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkFunctionIds, setBulkFunctionIds] = useState([]);
  const [bulkSide, setBulkSide] = useState(() => defaultSide);
  const [bulkVisibility, setBulkVisibility] = useState('Private');
  const [bulkWeddingOff, setBulkWeddingOff] = useState(false);

  useEffect(() => {
    if (inviteCooldown <= 0) return undefined;
    const timer = setTimeout(() => setInviteCooldown(seconds => Math.max(0, seconds - 1)), 1000);
    return () => clearTimeout(timer);
  }, [inviteCooldown]);

  const fnName = useCallback(
    (id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || '—',
    [functions]
  );

  // Every family of this event, for "put this guest into a family": the head,
  // how many people are in it, and a label to show in the picker.
  const familyOptions = useMemo(() => {
    const byId = new Map();
    rows.forEach(r => {
      const famId = Number(r.FamilyId || 0);
      if (!famId) return;
      if (!byId.has(famId)) byId.set(famId, { familyId: famId, head: null, size: 0, seen: new Set() });
      const fam = byId.get(famId);
      if (fam.seen.has(String(r.GuestId))) return;
      fam.seen.add(String(r.GuestId));
      fam.size += 1;
      if (String(r.GuestId) === String(famId)) fam.head = r;
    });
    return [...byId.values()]
      .filter(f => f.head)
      .map(f => ({
        familyId: f.familyId,
        head: f.head,
        size: f.size,
        label: [f.head.Surname, f.head.GuestName].filter(Boolean).join(' ') || 'Family',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  // "On the card of …": any existing family head, or any individual guest -
  // picking an individual makes them the head of a new card (value 'g:<GuestId>').
  const cardHeadOptions = useMemo(() => {
    const fams = familyOptions.map(f => ({ value: String(f.familyId), size: f.size, head: f.head, label: `${f.label} · family of ${f.size}` }));
    const singles = combineGuestRows(rows.filter(r => !Number(r.FamilyId || 0))).map(g => ({
      value: `g:${g.GuestId}`, size: 1, head: g, guestIds: g._guestIds || [g.GuestId],
      label: `${[g.Surname, g.GuestName, g.FatherHusband].filter(Boolean).join(' ') || 'Guest'} · own card${g.MobileNumber ? ` · ${g.MobileNumber}` : ''}`,
    }));
    return [...fams, ...singles].sort((a, b) => a.label.localeCompare(b.label));
  }, [familyOptions, rows]);

  const loadFunctions = useCallback(() => {
    GetFunctionsByEventId({ EventId: event.EventId })
      .then(d => setFunctions(Array.isArray(d) ? d : [])).catch(() => {});
  }, [event]);

  // Functions that can still be invited to (date today or later / no date).
  const invitableFunctions = useMemo(() => functions.filter(f => !isPastFunction(f)), [functions]);

  const refresh = useCallback((silent = false) => {
    if (silent !== true) setLoading(true);
    const loader = isAdmin
      ? GetGuestsByEventId({ EventId: event.EventId })
      : GetGuestsByEventIdForMember({ EventId: event.EventId, UserId: me });
    loader
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => toast('Failed to load members'))
      .finally(() => setLoading(false));
  }, [event, isAdmin, me, toast]);

  useEffect(() => { loadFunctions(); }, [loadFunctions]);
  useEffect(() => {
    GetTeamByEventId({ EventId: event.EventId }).then(d => setTeam(Array.isArray(d) ? d : [])).catch(() => setTeam([]));
  }, [event]);
  useEffect(() => { refresh(); }, [refresh]);

  const distinctPeople = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      const key = r.MobileNumber || `name:${r.GuestName}`;
      if (!map.has(key)) map.set(key, r);
    });
    return Array.from(map.values());
  }, [rows]);

  const addersByMobile = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      if (!r.MobileNumber) return;
      if (!m.has(r.MobileNumber)) m.set(r.MobileNumber, new Set());
      m.get(r.MobileNumber).add(String(r.CreatedBy));
    });
    return m;
  }, [rows]);
  const adderCount = (row) => (row.MobileNumber ? (addersByMobile.get(row.MobileNumber)?.size || 1) : 1);

  // Sort: { key, dir }. Keys map to a comparable value per row (see SORT_KEYS).
  // Clicking the same column header again flips the direction.
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const toggleSort = (key) => setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  // Who added the guests: one entry per person who has guests in this event,
  // with the number so two Sanjays can be told apart. Picking one turns the
  // list into THAT person's list - the cleanest way to work through a share of
  // the invitations - and the printed report then names them in its header
  // instead of repeating the name on every line.
  const adderOptions = useMemo(() => {
    const byUser = new Map();
    rows.forEach(r => {
      const id = String(r.CreatedBy || '');
      if (!id) return;
      if (!byUser.has(id)) {
        byUser.set(id, {
          value: id,
          name: (r.AddedByName || r.InvitedByName || '').trim() || `User ${id}`,
          mobile: String(r.AddedByMobile || '').trim(),
          count: 0,
        });
      }
      byUser.get(id).count += 1;
    });
    // You are always in the list, even before you have added anybody - the page
    // opens on you, so the dropdown must be able to show that.
    if (!byUser.has(String(me))) {
      byUser.set(String(me), {
        value: String(me),
        name: [user.Name, user.Surname].filter(Boolean).join(' ').trim() || 'You',
        mobile: String(user.MobileNumber || '').trim(),
        count: 0,
      });
    }
    return [...byUser.values()]
      .map(u => ({ ...u, label: `${u.name}${u.mobile ? ' - ' + u.mobile : ''}${String(u.value) === String(me) ? ' (you)' : ''}` }))
      .sort((a, b) => (String(b.value) === String(me)) - (String(a.value) === String(me)) || a.name.localeCompare(b.name));
  }, [rows, me, user]);
  // The page opens as YOUR list: 300 names from eight people is nobody's
  // working list, and the job in hand is "who have I invited, who am I still
  // missing". One click on "show everyone" beside the line under the toolbar
  // gives the whole event back, for this visit.
  const [adderFilter, setAdderFilter] = useState(String(me));
  const adder = adderOptions.find(u => u.value === String(adderFilter)) || null;
  // WHO a new guest is saved under. An admin working through someone else's
  // list ("Added by: Ailani Kajal") is adding to THAT list: every new guest,
  // family, family member, quick-add line and imported contact is saved with
  // their CreatedBy / AddedByName / AddedByMobile (the API then gives the guest
  // their side too). Everyone else - and an admin on their own list or on
  // everyone's - adds as themselves. (Bug 2026-10-20: it always saved as me.)
  const newOwner = useMemo(() => (isAdmin && adder && String(adder.value) !== String(me)
    ? { id: Number(adder.value), name: adder.name || null, mobile: adder.mobile || null, other: true }
    : {
      id: me, name: `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || null,
      mobile: user.MobileNumber || null, other: false,
    }), [isAdmin, adder, me, user]);

  // Groups (Friends Group, Rotary, UMA ...): the fixed list first, then any
  // other group already used in this event - one entry per spelling-without-
  // case, so "Electric Group" and "Electric group" are one group. Counts follow
  // the Added by filter, so "Haresh's Rotary people" reads right.
  const groupOptions = useMemo(() => {
    const scope = adderFilter ? rows.filter(r => String(r.CreatedBy || '') === String(adderFilter)) : rows;
    const byKey = new Map(GUEST_GROUPS.map(g => [groupKey(g), { value: g, count: 0 }]));
    let none = 0;
    scope.forEach(r => {
      const k = groupKey(r.GroupName);
      if (!k) { none += 1; return; }
      if (!byKey.has(k)) byKey.set(k, { value: String(r.GroupName).trim(), count: 0 });
      byKey.get(k).count += 1;
    });
    const fixed = GUEST_GROUPS.map(g => byKey.get(groupKey(g)));
    const others = [...byKey.values()].filter(o => !GUEST_GROUPS.some(g => groupKey(g) === groupKey(o.value)))
      .sort((a, b) => a.value.localeCompare(b.value));
    return { list: [...fixed, ...others], none };
  }, [rows, adderFilter]);
  const groupNames = useMemo(() => groupOptions.list.map(o => o.value), [groupOptions]);
  const [groupFilter, setGroupFilter] = useState('');   // '' all · '__none' no group · a group

  // all · family (in a household) · individual (on their own) · business (a firm)
  const [guestTypeFilter, setGuestTypeFilter] = useState('all');
  const [visFilter, setVisFilter] = useState('');   // '' all · 'public' · 'private'
  const isBusiness = (r) => String(r?.GuestType || 'Family') === 'Business';

  const filtered = useMemo(() => {
    let list = rows;
    if (fnFilter === '__all' || fnFilter === '__some') {
      const open = functions.filter(f => !isPastFunction(f)).map(f => String(f.FunctionId));
      const hasAll = (r) => { const ids = rowFunctionIds(r); return open.every(id => ids.includes(id)); };
      list = list.filter(r => (fnFilter === '__all' ? hasAll(r) : !hasAll(r)));
    } else if (fnFilter) {
      const want = fnMode !== 'out';
      list = list.filter(r => rowFunctionIds(r).includes(String(fnFilter)) === want);
    }
    if (adderFilter) list = list.filter(r => String(r.CreatedBy || '') === String(adderFilter));
    if (groupFilter === '__none') list = list.filter(r => !groupKey(r.GroupName));
    else if (groupFilter) list = list.filter(r => groupKey(r.GroupName) === groupKey(groupFilter));
    // Groom / Bride buttons; the old Male / Female values count as Groom / Bride.
    if (sideFilter) list = list.filter(r => sideKey(r.Side) === sideFilter);
    if (cardFilter) list = list.filter(r => cardRole(r) === 'own' || (cardFilter === 'cards' && cardRole(r) === 'head'));
    // Guest type: 'all' · 'relative' (stored 'Family') · 'friend' · 'business'.
    if (guestTypeFilter && guestTypeFilter !== 'all') {
      const want = guestTypeFilter === 'relative' ? 'family' : guestTypeFilter;
      list = list.filter(r => String(r.GuestType || 'Family').toLowerCase() === want);
    }
    // Public / Private dropdown ('' = both). Public = Visibility 'Common'.
    if (visFilter) list = list.filter(r => ((r.Visibility || 'Private') === 'Common') === (visFilter === 'public'));
    // Search the guest's OWN details only - not who added them ("ailani" must
    // not match every guest Sanjay Ailani added). Every typed word must match,
    // in any order ("sanjay ailani" = "ailani sanjay").
    const words = String(q || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length) {
      const text = (r) => [r.Surname, r.GuestName, r.FatherHusband, r.MobileNumber, r.City, r.CompanyName,
        r.CardName, r.Relation, r.GroupName, r.Remarks].filter(Boolean).join(' ').toLowerCase();
      list = list.filter(r => { const t = text(r); return words.every(w => t.includes(w)); });
    }
    const get = SORT_KEYS[sort.key] || SORT_KEYS.name;
    const dir = sort.dir === 'desc' ? -1 : 1;
    return combineGuestRows(list).sort((a, b) => {
      const va = get(a), vb = get(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base', numeric: true }) * dir;
    });
  }, [rows, functions, fnFilter, fnMode, adderFilter, groupFilter, sideFilter, cardFilter, guestTypeFilter, visFilter, q, sort]);
  const visCounts = useMemo(() => {
    const c = { all: 0, public: 0, private: 0 };
    combineGuestRows(rows).forEach(g => { c.all += 1; c[(g.Visibility || 'Private') === 'Common' ? 'public' : 'private'] += 1; });
    return c;
  }, [rows]);
  const typeCounts = useMemo(() => {
    const c = { all: 0, relative: 0, friend: 0, business: 0, home: 0 };
    combineGuestRows(rows).forEach(g => {
      const t = String(g.GuestType || 'Family').toLowerCase();
      c.all += 1;
      const k = t === 'family' ? 'relative' : t;
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [rows]);
  // Guests invited to each function (all guests this user sees, before any
  // filter) - shown next to every function name: "Wedding (123)".
  const fnCounts = useMemo(() => {
    const m = new Map();
    rows.forEach(r => rowFunctionIds(r).forEach(id => m.set(id, (m.get(id) || 0) + 1)));
    return m;
  }, [rows]);
  const fnCount = useCallback((id) => fnCounts.get(String(id)) || 0, [fnCounts]);
  // A line is not always one person (a count-only group, a guest with extra
  // members), so the function filter says both: "408 · 532 persons" - the
  // persons being what the Brief Report and the caterer count.
  const fnPersonsMap = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      const n = Math.max(1, Number(r.ExpectedCount || 1));
      rowFunctionIds(r).forEach(id => m.set(id, (m.get(id) || 0) + n));
    });
    return m;
  }, [rows]);
  const fnCountText = useCallback((id, short = false) => {
    const lines = fnCounts.get(String(id)) || 0;
    const persons = fnPersonsMap.get(String(id)) || 0;
    if (persons <= lines) return String(lines);
    return short ? `${lines} · ${persons}p` : `${lines} · ${persons} persons`;
  }, [fnCounts, fnPersonsMap]);
  // Words for the function filter (toolbar tooltip, export header).
  // ONE function chosen from the filter ("Cocktail Party", Invited): the list
  // is then that party's list - its own guests, its own columns, its own
  // heading. `__all` / `__some` / "Not invited" are not one party, so they keep
  // the whole table.
  const singleFunction = useMemo(() => (
    fnFilter && fnFilter !== '__all' && fnFilter !== '__some' && fnMode !== 'out'
      ? functions.find(f => String(f.FunctionId) === String(fnFilter)) || null : null
  ), [fnFilter, fnMode, functions]);
  // "Cocktail Party · 26-11-2026 · 8:00 PM – 11:00 PM · Town Hall"
  const partyLine = useMemo(() => {
    if (!singleFunction) return '';
    const f = singleFunction;
    const time = [fmtTime(f.StartTime || f.FunctionTime), f.EndTime ? fmtTime(f.EndTime) : ''].filter(Boolean).join(' – ');
    return [f.FunctionName, fmtDate(f.FunctionDate), time, f.Venue].filter(Boolean).join(' · ');
  }, [singleFunction]);

  const fnFilterLabel = fnFilter === '__all' ? 'Invited to all functions'
    : fnFilter === '__some' ? 'Missing some functions'
      : fnFilter ? `${fnMode === 'out' ? 'Not invited to' : 'Invited to'} ${fnName(fnFilter)}` : 'All functions';

  // ── Desktop list view: 'people' (all guests, a row each), 'families' (only
  // families: the head, its members folded underneath) or 'individuals' (only
  // guests in no family). Remembered per browser.
  const [listView, setListViewRaw] = useState(() => {
    try { const v = localStorage.getItem('se_guest_view'); return ['families', 'individuals', 'business'].includes(v) ? v : 'people'; } catch { return 'people'; }
  });
  const setListView = (v) => { setListViewRaw(v); try { localStorage.setItem('se_guest_view', v); } catch { /* ignore */ } };
  // The filtered + sorted list grouped by family, in list order. A family whose
  // head is filtered out is led by its first listed member (headMissing).
  // The Families view shows a household in the SAME order the check list
  // prints it, so moving someone up here is seen on the sheet.
  const familyGroups = useMemo(() => {
    const groups = [];
    const byFam = new Map();
    filtered.forEach(r => {
      const f = Number(r.FamilyId || 0);
      if (!f) { groups.push({ key: `g${r.GuestId}`, head: r, members: [] }); return; }
      if (!byFam.has(f)) { const g = { key: `f${f}`, familyId: f, head: null, members: [] }; byFam.set(f, g); groups.push(g); }
      const g = byFam.get(f);
      if (Number(r.GuestId) === f) g.head = r; else g.members.push(r);
    });
    groups.forEach(g => {
      if (!g.head) { g.head = g.members.shift(); g.headMissing = true; }
      g.members = sortFamilyMembers(g.members);
    });
    return groups.filter(g => g.familyId);
  }, [filtered]);
  const individualRows = useMemo(() => filtered.filter(r => !Number(r.FamilyId || 0)), [filtered]);
  // The firms: a business associate is one row (the owner, with his company).
  const businessRows = useMemo(() => filtered.filter(isBusiness), [filtered]);   // eslint-disable-line react-hooks/exhaustive-deps
  const viewCounts = {
    people: filtered.length, families: familyGroups.length,
    individuals: individualRows.length, business: businessRows.length,
  };

  // ── Family cards (mobile list) ────────────────────────────────────────────
  // Group the (already filtered + sorted) rows into cards: individuals as-is,
  // family members collapsed under their head. Order follows `filtered`.
  const familyCards = useMemo(() => {
    const cards = [];
    const families = new Map();
    const allFamilyMembers = new Map();
    rows.forEach(row => {
      const familyId = Number(row.FamilyId || 0);
      if (!familyId) return;
      if (!allFamilyMembers.has(familyId)) allFamilyMembers.set(familyId, []);
      if (!allFamilyMembers.get(familyId).some(m => String(m.GuestId) === String(row.GuestId))) allFamilyMembers.get(familyId).push(row);
    });
    filtered.forEach(row => {
      const familyId = Number(row.FamilyId || 0);
      if (!familyId) { cards.push({ type: 'guest', key: `guest-${row.GuestId}`, row }); return; }
      if (!families.has(familyId)) {
        const family = { type: 'family', key: `family-${familyId}`, familyId, members: [] };
        families.set(familyId, family);
        cards.push(family);
      }
      families.get(familyId).members = allFamilyMembers.get(familyId) || [row];
    });
    families.forEach(family => {
      family.members.sort((a, b) => {
        if (String(a.GuestId) === String(family.familyId)) return -1;
        if (String(b.GuestId) === String(family.familyId)) return 1;
        return Number(a.GuestId) - Number(b.GuestId);
      });
      family.head = family.members.find(r => String(r.GuestId) === String(family.familyId)) || family.members[0];
    });
    return cards;
  }, [filtered, rows]);
  const visibleCards = useMemo(() => familyCards.filter(card =>
    guestTypeFilter === 'all'
      || (guestTypeFilter === 'family' && card.type === 'family')
      || (guestTypeFilter === 'individual' && card.type === 'guest')
      || (guestTypeFilter === 'business' && isBusiness(card.type === 'family' ? card.head : card.row))
  ), [familyCards, guestTypeFilter]);

  // Family-member actions are rate-limited to one per 5 s (accidental double taps).
  const [actionCooldown, setActionCooldown] = useState(0);
  useEffect(() => {
    if (actionCooldown <= 0) return undefined;
    const timer = setTimeout(() => setActionCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [actionCooldown]);
  const beginFamilyAction = (action) => {
    if (actionCooldown > 0) return;
    setActionCooldown(5);
    action();
  };
  // Open the phone dialer (APK bridge when present, tel: otherwise).
  const callGuest = (row) => {
    const mobile = String(row.MobileNumber || '').replace(/\D/g, '');
    if (!mobile) return;
    if (window.AndroidDialer?.openDialer) window.AndroidDialer.openDialer(mobile);
    else window.location.href = `tel:${mobile}`;
  };

  // Guests per card role (whole list) for the All / Own + Head / Only own buttons.
  const cardCounts = useMemo(() => {
    const c = { all: 0, own: 0, head: 0, member: 0 };
    combineGuestRows(rows).forEach(g => { c.all += 1; c[cardRole(g)] += 1; });
    return { all: c.all, cards: c.own + c.head, own: c.own };
  }, [rows]);

  // Guests per side (whole list, one per person) for the Groom / Bride buttons.
  const sideCounts = useMemo(() => {
    const c = { all: 0, Groom: 0, Bride: 0, Common: 0 };
    combineGuestRows(rows).forEach(g => { c.all += 1; c[sideKey(g.Side)] = (c[sideKey(g.Side)] || 0) + 1; });
    return c;
  }, [rows]);

  const stats = useMemo(() => {
    const persons = filtered.reduce((sum, r) => sum + Number(r.ExpectedCount || 1), 0);
    const entries = filtered.reduce((sum, r) => sum + (r._rows?.length || 1), 0);
    const bride = filtered.filter(r => r.Side === 'Bride' || r.Side === 'Female').length;
    const groom = filtered.filter(r => r.Side === 'Groom' || r.Side === 'Male').length;
    return { entries, parties: filtered.length, persons, bride, groom };
  }, [filtered]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const selectedFunctionIds = useMemo(() => (Array.isArray(form.FunctionIds) ? form.FunctionIds.map(String) : []), [form.FunctionIds]);
  const toggleFunction = (functionId) => {
    const id = String(functionId);
    const fn = functions.find(f => String(f.FunctionId) === id);
    if (fn && isPastFunction(fn)) return; // over — cannot be (un)ticked
    const res = toggleWithWedding(selectedFunctionIds, id, functions, weddingOff);
    setWeddingOff(res.weddingOff);
    setForm(f => ({ ...f, FunctionIds: res.ids, FunctionId: res.ids[0] || '' }));
  };
  // What the guest form offers: every open function, plus (when editing) the
  // past functions this guest is already in — shown disabled, so history is
  // visible but nothing new can be added to a function that is over.
  const pickerFunctions = useMemo(() => functions
    .filter(f => !isPastFunction(f) || (editing && selectedFunctionIds.includes(String(f.FunctionId))))
    .map(f => ({ ...f, past: isPastFunction(f) })),
    [functions, editing, selectedFunctionIds]);

  // Tick / untick every open function in one tap (mobile chips).
  const allOpenIds = useMemo(() => functions.filter(f => !isPastFunction(f)).map(f => String(f.FunctionId)), [functions]);
  const allFunctionsSelected = allOpenIds.length > 0 && allOpenIds.every(id => selectedFunctionIds.includes(id));
  const toggleAllFunctions = () => setForm(f => {
    const next = allFunctionsSelected ? [] : allOpenIds;
    return { ...f, FunctionIds: next, FunctionId: next[0] || '' };
  });

  // A family member may be invited to FEWER functions than the head (children
  // to the Haldi only, a colleague to the reception). An empty FunctionIds
  // means "same as the head", which is the common case.
  const addParty = () => setParty(p => (p.length >= MAX_FAMILY_SIZE - 1 ? p : [...p, blankMember()]));
  const togglePartyFunction = (i, functionId) => setParty(p => p.map((m, idx) => {
    if (idx !== i) return m;
    const own = Array.isArray(m.FunctionIds) && m.FunctionIds.length ? m.FunctionIds.map(String) : selectedFunctionIds;
    // Wedding rule, limited to the head's functions (a member never gets more).
    const headPool = functions.filter(f => selectedFunctionIds.includes(String(f.FunctionId)));
    const res = toggleWithWedding(own, functionId, headPool, !!m.weddingOff);
    const next = res.ids;
    // Back to the head's full set → store nothing, so it keeps following it.
    const same = next.length === selectedFunctionIds.length && selectedFunctionIds.every(x => next.includes(x));
    return { ...m, FunctionIds: same ? [] : next, weddingOff: res.weddingOff };
  }));
  // What this member is actually invited to, head's list included.
  const partyFunctionIds = (m) => (Array.isArray(m.FunctionIds) && m.FunctionIds.length
    ? m.FunctionIds.map(String).filter(id => selectedFunctionIds.includes(id))
    : selectedFunctionIds);
  const setPartyField = (i, k, v) => setParty(p => p.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const removeParty = (i) => setParty(p => p.filter((_, idx) => idx !== i));

  const openAdd = (mode = 'individual') => {
    if (sideMissing) { toast('Ask your event admin to set your side (Groom or Bride) before adding guests'); return; }
    const preset = functions.find(f => String(f.FunctionId) === String(fnFilter));
    const remembered = readDefaults(event.EventId);
    // Preselect: the filtered function, else the functions used last time (still open).
    const openIds = new Set(functions.filter(f => !isPastFunction(f)).map(f => String(f.FunctionId)));
    const rememberedIds = (remembered.FunctionIds || []).map(String).filter(id => openIds.has(id));
    // The LAST guest's functions are repeated EXACTLY. Invite someone to the
    // Thread Ceremony and the Reception and the next guest starts with those
    // two - the auto rule (which adds the Wedding when another function is
    // ticked) belongs to ticking a box by hand, not to repeating a set that
    // was just used and saved. A set that leaves an auto function out carries
    // `weddingOff`, so it stays left out while more are ticked.
    const usedPreset = preset && !isPastFunction(preset);
    const initialFunctions = usedPreset
      ? newGuestFunctionIds([String(fnFilter)], functions)
      : (rememberedIds.length ? rememberedIds : newGuestFunctionIds([], functions));
    const missesAuto = openAutoFunctionIds(functions).some(id => !initialFunctions.includes(id));
    setAddedThisSession(0);
    setWeddingOff(!usedPreset && rememberedIds.length > 0 && missesAuto);
    setForm({
      ...blankForm(),
      Side: lockedSide || (isMarriage && remembered.Side) || defaultSide,
      // ALWAYS Private to start with - picking Public once must not quietly
      // make every later guest Public (functions / side / surname are still
      // remembered, those cost nothing to get wrong).
      Visibility: 'Private',
      FunctionId: initialFunctions[0] || '', FunctionIds: initialFunctions,
    });
    setFamilyMode(mode === 'family');
    setParty(mode === 'family' ? blankMembers() : []);
    setAddMode('new'); setDupes([]); setCheckedMobile(''); setEditing(null); setOpen(true);
    setExistingNotice('');
    setFamilyHeadNote('');
    people.clear();
  };
  // Right-click → "Add family members (make head)": Add Family with this guest
  // already filled in as the head (name, number, their functions). Saving
  // merges as usual (saveFamilyMerged): the guest becomes the head, existing
  // members of their family stay. A guest on SOMEONE ELSE's card is warned
  // first - making them a head takes them out of that family.
  const [familyHeadNote, setFamilyHeadNote] = useState('');
  const openFamilyFor = (row) => {
    if (!(mine(row) && canAdd)) { toast('Only the member who added this guest (or an admin) can do this'); return; }
    const who = [row.Surname, row.GuestName].filter(Boolean).join(' ') || 'This guest';
    const famId = Number(row.FamilyId || 0);
    const isHead = famId && famId === Number(row.GuestId);
    let note = '';
    if (famId && !isHead) {
      const fam = familyOptions.find(f => Number(f.familyId) === famId);
      const headName = fam ? [fam.head.Surname, fam.head.GuestName].filter(Boolean).join(' ') : 'another guest';
      if (!window.confirm(`${who} is already on ${headName}'s card (${headName} is the head of that family).\n\nMake ${who} the head of a family of their own instead? They will leave ${headName}'s family.`)) return;
      note = `${who} leaves ${headName}'s family and becomes the head of a new one.`;
    } else if (isHead) {
      const size = rows.filter(x => Number(x.FamilyId || 0) === famId).length;
      note = `${who} is already the head of a family of ${size} - the members you add here join it; existing members stay.`;
    }
    openAdd('family');
    const ids = [...new Set([...rowFunctionIds(row), ...(row._functionIds || [])].map(String))];
    setForm(f => ({
      ...f,
      Surname: row.Surname || '', GuestName: row.GuestName || '', FatherHusband: row.FatherHusband || '',
      MobileNumber: row.MobileNumber || '',
      FunctionIds: ids.length ? ids : f.FunctionIds, FunctionId: (ids[0] || f.FunctionId),
      Visibility: row.Visibility || f.Visibility,
    }));
    setFamilyHeadNote(note);
  };

  // Back-key support: reset the form to blank but keep it open (clear-then-close).
  const clearForm = () => {
    const preset = functions.find(f => String(f.FunctionId) === String(fnFilter));
    const initialFunctions = newGuestFunctionIds(preset && !isPastFunction(preset) ? [String(fnFilter)] : [], functions);
    setWeddingOff(false);
    setForm({ ...blankForm(), Side: lockedSide || defaultSide, FunctionId: initialFunctions[0] || '', FunctionIds: initialFunctions });
    setParty(familyMode ? blankMembers() : []);
    setAddMode('new'); setDupes([]); setCheckedMobile('');
  };
  // Dirty = the user typed anything meaningful (lead fields or any party rows).
  const isDirty = () => {
    const b = blankForm();
    const changed = ['GuestName', 'Surname', 'FatherHusband', 'MobileNumber', 'Relation', 'GroupName', 'Remarks']
      .some(k => String(form[k] ?? '') !== String(b[k] ?? ''));
    return changed || party.some(m => String(m.GuestName || '').trim() || String(m.MobileNumber || '').trim());
  };
  const openEdit = (row, { existing = false } = {}) => {
    // A count-only group ("School Friends - 6") has its own small form.
    if (isCountGroup(row)) { countGroup.openGroupEdit(row); return; }
    const functionIds = rowFunctionIds(row);
    const famId = Number(row.FamilyId || 0);
    const onOtherCard = !!famId && famId !== Number(row.GuestId);
    setExistingNotice(existing ? ([row.Surname, row.GuestName, row.FatherHusband].filter(Boolean).join(' ') || 'This person') : '');
    // A guest saved WITHOUT the Wedding had it removed on purpose - keep it off.
    setWeddingOff(functionIds.length > 0 && !functionIds.some(id => isWeddingFunction(functions.find(f => String(f.FunctionId) === String(id)))));
    setForm({
      FunctionId: functionIds[0] || '', FunctionIds: functionIds,
      Surname: row.Surname ?? '', GuestName: row.GuestName ?? '',
      FatherHusband: row.FatherHusband ?? '', MobileNumber: row.MobileNumber ?? '',
      Side: row.Side ?? 'Common', Relation: row.Relation ?? '',
      // A row saved before the two fields were merged may carry only the old
      // type: show it as the group it means, so editing does not lose it.
      GroupName: row.GroupName || groupForGuestType(row.GuestType) || '',
      EmailId: row.EmailId ?? '', ExpectedCount: 1,
      GuestType: row.GuestType || 'Family', CompanyName: row.CompanyName ?? '', City: row.City ?? '',
      Visibility: row.Visibility ?? 'Private',
      InviteStatus: row.InviteStatus ?? 'Pending', InvitedVia: row.InvitedVia ?? '', Remarks: row.Remarks ?? '',
      ExtraCards: Number(row.ExtraCards || 0) ? String(row.ExtraCards) : '',
      CardMode: onOtherCard ? 'family' : 'own', CardFamilyId: onOtherCard ? String(famId) : '',
      CardName: row.CardName ?? '', CardDeliveredByName: row.CardDeliveredByName ?? '',
      CardNameAuto: !String(row.CardName || '').trim(),
    });
    setFamilyMode(false);
    setParty([]); setAddMode('new'); setDupes([]); setCheckedMobile(''); setEditing(row); setOpen(true);
  };

  // Type-ahead help: the surnames and first names already in this event, so a
  // family is spelt the same way every time instead of Punjabi / punjabee.
  const knownSurnames = useMemo(() => [...new Set(rows.map(r => String(r.Surname || '').trim()).filter(Boolean))].sort(), [rows]);
  const knownNames = useMemo(() => [...new Set(rows.map(r => String(r.GuestName || '').trim()).filter(Boolean))].sort(), [rows]);

  // Same person twice? A guest is "already saved" when the SAME surname+name,
  // or the SAME number, is already invited to one of the functions being
  // ticked - whoever added it. Re-inviting the same person to a DIFFERENT
  // function is normal and stays allowed.
  const personKey = (surname, name) => `${lc(surname)}|${lc(name)}`;
  const nameMatches = (surname, name, exceptGuestId) => {
    const key = personKey(surname, name);
    if (key === '|') return [];
    return rows.filter(r => personKey(r.Surname, r.GuestName) === key
      && String(r.GuestId) !== String(exceptGuestId || ''));
  };
  const clashingFunctions = (list) => list.filter(r => rowFunctionIds(r).some(id => selectedFunctionIds.includes(String(id))));
  const whoAdded = (r) => (String(r.CreatedBy) === String(me) ? 'you' : ((r.AddedByName || r.InvitedByName || '').trim() || 'another member'));

  const canImport = contactsSupported();
  // "Card delivered by" suggestions: the team + names already used.
  const deliveryNames = useMemo(() => [...new Set([
    ...team.map(t => [t.Name, t.Surname].filter(Boolean).join(' ').trim()),
    ...rows.map(r => String(r.CardDeliveredByName || '').trim()),
  ].filter(Boolean))].sort(), [team, rows]);

  // One contact can hold a dozen numbers (both SIMs, the office, the sons).
  // When it does we ask which one to invite instead of guessing the first;
  // `numberChoice` drives that little chooser - { contact, target } where
  // target is 'head' or the family-member index.
  const [numberChoice, setNumberChoice] = useState(null);

  const applyContact = async (c, mobile, target) => {
    if (target === 'head') {
      setForm(f => ({
        ...f,
        Surname: normaliseText(c.surname) || f.Surname,
        GuestName: normaliseText(c.name) || f.GuestName,
        FatherHusband: normaliseText(c.fatherHusband) || f.FatherHusband,
        MobileNumber: mobile || f.MobileNumber,
      }));
      if (mobile) await checkMobile(mobile);
      return;
    }
    setParty(p => p.map((m, idx) => (idx === target ? {
      ...m,
      Surname: normaliseText(c.surname) || m.Surname,
      GuestName: normaliseText(c.name) || m.GuestName,
      MobileNumber: mobile || m.MobileNumber,
    } : m)));
  };

  const importInto = async (target) => {
    try {
      const c = await pickContactFromDevice();
      if (!c) return;
      const numbers = c.numbers || (c.mobile ? [{ number: c.mobile, label: '', mobile: true }] : []);
      if (numbers.length > 1) { setNumberChoice({ contact: c, numbers, target }); return; }
      await applyContact(c, numbers[0]?.number || c.mobile, target);
    } catch (e) { toast(e.message || 'Could not read contacts'); }
  };

  const importContact = () => importInto('head');
  // The chooser calls this with the number the user tapped.
  const chooseContactNumber = async (mobile) => {
    const pending = numberChoice;
    setNumberChoice(null);
    if (pending) await applyContact(pending.contact, mobile, pending.target);
  };

  // Same phone-book pick, but into one family-member row: a family is usually
  // several entries of the same phone book, so typing them out is wasted work.
  const importPartyContact = (i) => importInto(i);

  const pickExisting = (guestId) => {
    const r = rows.find(x => String(x.GuestId) === String(guestId));
    if (!r) return;
    setForm(f => ({
      ...f,
      Surname: r.Surname ?? '', GuestName: r.GuestName ?? '', FatherHusband: r.FatherHusband ?? '',
      MobileNumber: r.MobileNumber ?? '', Side: r.Side ?? 'Common',
      Relation: r.Relation ?? '', GroupName: r.GroupName ?? '',
      EmailId: r.EmailId ?? '', ExpectedCount: r.ExpectedCount ?? 1,
    }));
    checkMobile(r.MobileNumber);
  };

  const checkMobile = async (mobile) => {
    const m = String(mobile || '').trim();
    if (!m) { setDupes([]); setCheckedMobile(''); return []; }
    let res = [];
    try {
      const r = await GetGuestsByEventIdAndMobile({ EventId: event.EventId, MobileNumber: m });
      res = Array.isArray(r) ? r : [];
    } catch { res = []; }
    setDupes(res); setCheckedMobile(m);
    return res;
  };

  // Typing a complete 10-digit number runs the duplicate check straight away.
  useEffect(() => {
    const m = String(form.MobileNumber || '').trim();
    if (!open || editing || familyMode || !/^\d{10}$/.test(m) || m === checkedMobile) return undefined;
    const t = setTimeout(() => { checkMobile(m); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.MobileNumber, open, editing, familyMode, checkedMobile]);
  // The number already belongs to a guest of this event I may edit (mine, or
  // anyone's for an admin) → switch the form to that guest, functions ticked,
  // instead of the "already added" dead end.
  useEffect(() => {
    if (!open || editing || familyMode || !dupes.length) return;
    const d = dupes.find(x => mine(x));
    // The whole list (not the filtered view), grouped like the cards on screen.
    const row = d && combineGuestRows(rows).find(g => (g._guestIds || [g.GuestId]).map(String).includes(String(d.GuestId)));
    if (row) openEdit(row, { existing: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dupes]);

  const blockedSameFn = !editing && !familyMode && dupes.some(
    d => String(d.CreatedBy) === String(me) && rowFunctionIds(d).some(id => selectedFunctionIds.includes(String(id)))
  );
  const hasOtherInvites = !editing && dupes.length > 0 && !blockedSameFn;
  const needsCheck = !editing && checkedMobile !== String(form.MobileNumber).trim();

  // ── Add Family MERGES people who are already guests of this event ────────
  // A person already in the list (same mobile; without one, same surname +
  // name) is not created again: the head becomes this family's head, members
  // are MOVED under it (even out of another family), and everyone keeps their
  // functions plus the ones ticked here. Only new people are created.
  const allPeople = useMemo(() => combineGuestRows(rows), [rows]);
  const findExisting = useCallback((mobile, surname, name, exceptId) => {
    const m = String(mobile || '').trim();
    const pool = allPeople.filter(g => String(g.GuestId) !== String(exceptId || ''));
    // The number is the surest match - but HALF the guests of this event have
    // none, so a number that matches nobody must fall through to the name.
    // (Typing a number for "Kewalramani Gulab", who was imported without one,
    // used to create a second Kewalramani Gulab.)
    if (/^\d{10}$/.test(m)) {
      const byMobile = pool.find(g => String(g.MobileNumber || '') === m);
      if (byMobile) return byMobile;
    }
    const key = `${lc(surname)}|${lc(name)}`;
    if (!lc(name)) return null;
    // Only someone who ALSO has no number: two people of one name with two
    // different numbers are two people.
    return pool.find(g => `${lc(g.Surname)}|${lc(g.GuestName)}` === key
      && !(/^\d{10}$/.test(m) && /^\d{10}$/.test(String(g.MobileNumber || '')))) || null;
  }, [allPeople]);
  // For the family form: is the head / member i already a guest?
  const familyHeadExisting = familyMode && open && !editing ? findExisting(form.MobileNumber, form.Surname, form.GuestName) : null;
  const familyMemberExisting = (m) => (familyMode ? findExisting(m.MobileNumber, m.Surname || form.Surname, m.GuestName, familyHeadExisting?.GuestId) : null);
  // The people ALREADY in that family, when the typed head turns out to be an
  // existing family head: the form lists them so the user sees who is there
  // before adding more (they are not touched by the save).
  const existingFamilyMembers = useMemo(() => {
    const headId = Number(familyHeadExisting?.GuestId || 0);
    if (!headId) return [];
    const seen = new Set();
    return rows.filter(r => {
      if (Number(r.FamilyId || 0) !== headId) return false;
      if (Number(r.GuestId) === headId) return false;          // the head is row 1
      if (seen.has(String(r.GuestId))) return false;           // one row per person
      seen.add(String(r.GuestId));
      return true;
    });
  }, [rows, familyHeadExisting]);

  // A member the family ALREADY has can be corrected right here - a wrong
  // surname ("Naresh" where the family is "Talreja") was only fixable from the
  // guest list, which means leaving the half-typed family behind. Typing shows
  // at once; leaving the box saves that person's row(s).
  const [savingExisting, setSavingExisting] = useState(null);   // GuestId being written
  const setExistingMemberField = (guest, key, value) => {
    const v = key === 'MobileNumber' ? tenDigits(value) : nameInput(value);
    const ids = new Set(guestIdsOf(guest).map(String));
    setRows(rs => rs.map(r => (ids.has(String(r.GuestId)) ? { ...r, [key]: v } : r)));
  };
  const commitExistingMember = async (guest, original) => {
    const ids = guestIdsOf(guest);
    const fresh = rows.find(r => String(r.GuestId) === String(guest.GuestId)) || guest;
    const data = {};
    ['Surname', 'GuestName', 'MobileNumber', 'Relation'].forEach(k => {
      const was = String(original?.[k] ?? '').trim();
      const now = String(fresh[k] ?? '').trim();
      if (was !== now) data[k] = now || null;
    });
    if (!Object.keys(data).length || !ids.length) return;
    if (!data.GuestName && 'GuestName' in data) { toast('A name is needed'); refresh(true); return; }
    setSavingExisting(String(guest.GuestId));
    try {
      await UpdateGuest({ data: { ...data, UpdatedBy: me }, where: `GuestId in (${ids.join(',')}) and EventId = ${Number(event.EventId)}` });
    } catch {
      toast('Could not save - check your connection');
      refresh(true);
    } finally {
      setSavingExisting(null);
    }
  };

  const idsOfPerson = (g) => [...new Set([...rowFunctionIds(g), ...(g._functionIds || [])].map(String))];
  const guestIdsOf = (g) => (g._guestIds && g._guestIds.length ? g._guestIds : [g.GuestId]).map(Number);
  // Members of another family whose head is g (g cannot be pulled away then).
  const headsOthers = (g) => Number(g.FamilyId || 0) === Number(g.GuestId)
    && rows.some(x => Number(x.FamilyId || 0) === Number(g.GuestId) && Number(x.GuestId) !== Number(g.GuestId));

  // ── Merge duplicates: other guests with the SAME surname + name ─────────
  // (one person entered several times with different numbers). Opening one of
  // them lists the others; merging keeps the opened guest, gives it every
  // function of the others, moves the families they head under it, notes their
  // numbers in Remarks, keeps the larger extra-card count, and removes them.
  const sameNameGuests = useMemo(() => {
    if (!editing) return [];
    const key = `${lc(editing.Surname)}|${lc(editing.GuestName)}`;
    return allPeople.filter(g => String(g.GuestId) !== String(editing.GuestId)
      && `${lc(g.Surname)}|${lc(g.GuestName)}` === key);
  }, [editing, allPeople]);
  // Adding ONE guest: anyone of this event already carrying that exact
  // surname + name. Not a block - two cousins really are both "Ailani Sneha",
  // and they then have two numbers - but it is said while the form is open,
  // with the way to open that guest instead of filing a second one.
  const nameTwins = useMemo(() => {
    if (editing || familyMode || !open) return [];
    const key = `${lc(form.Surname)}|${lc(form.GuestName)}`;
    if (!lc(form.GuestName) || !lc(form.Surname)) return [];
    const typed = String(form.MobileNumber || '').trim();
    return allPeople.filter(g => `${lc(g.Surname)}|${lc(g.GuestName)}` === key
      // A different number is a different person - that is already said by the
      // number check, which lists them under "Already added in this event".
      && !(/^\d{10}$/.test(typed) && /^\d{10}$/.test(String(g.MobileNumber || '')) && String(g.MobileNumber) !== typed));
  }, [editing, familyMode, open, form.Surname, form.GuestName, form.MobileNumber, allPeople]);
  const openTwin = (row) => openEdit(row, { existing: true });

  const [mergePick, setMergePick] = useState(() => new Set());
  useEffect(() => { setMergePick(new Set()); }, [editing]);
  const toggleMergePick = (id) => setMergePick(s => { const n = new Set(s); const k = String(id); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const [merging, setMerging] = useState(false);
  const mergeDuplicates = async () => {
    const keep = editing;
    const dups = sameNameGuests.filter(g => mergePick.has(String(g.GuestId)));
    if (!keep || !dups.length) return;
    if (!dups.every(mine)) { toast('Some of these were added by another member - only an admin can merge them'); return; }
    const who = [keep.Surname, keep.GuestName, keep.FatherHusband].filter(Boolean).join(' ');
    if (!window.confirm(`Merge ${dups.length} duplicate${dups.length > 1 ? 's' : ''} into ${who} (${keep.MobileNumber || 'no number'})?\nTheir functions and family members move here; the duplicates are removed.`)) return;
    setMerging(true);
    try {
      const keepId = Number(keep.GuestId);
      const keepIds = guestIdsOf(keep);
      const fns = new Set(idsOfPerson(keep));
      const numbers = [];
      let extra = Number(keep.ExtraCards || 0);
      let keepFam = Number(keep.FamilyId || 0);
      for (const d of dups) {
        idsOfPerson(d).forEach(f => fns.add(f));
        if (d.MobileNumber && d.MobileNumber !== keep.MobileNumber) numbers.push(d.MobileNumber);
        extra = Math.max(extra, Number(d.ExtraCards || 0));
        const dId = Number(d.GuestId);
        if (Number(d.FamilyId || 0) === dId) {
          // d headed a family: its members (and d itself) move under keep.
          await UpdateGuest({ data: { FamilyId: keepId, UpdatedBy: me }, where: `FamilyId = ${dId} and EventId = ${Number(event.EventId)}` });
          if (!keepFam || keepFam !== keepId) keepFam = keepId;
        } else if (Number(d.FamilyId || 0) && !keepFam) {
          keepFam = Number(d.FamilyId);   // d was on someone's card - keep goes there
        }
      }
      const remark = numbers.length ? `Other numbers: ${[...new Set(numbers)].join(', ')}` : '';
      await UpdateGuest({
        data: {
          FamilyId: keepFam || 0, ExtraCards: extra,
          ...(remark ? { Remarks: [keep.Remarks, remark].filter(Boolean).join(' · ').slice(0, 500) } : {}),
          UpdatedBy: me,
        },
        where: `GuestId in (${keepIds.join(',')})`,
      });
      await SetGuestFunctions({
        GuestId: keepId, EventId: event.EventId, OrganiserId: organiser.OrganiserId, FunctionIds: [...fns], UpdatedBy: me,
      });
      const dupIds = dups.flatMap(guestIdsOf);
      await UpdateGuest({ data: { Valid: 0, UpdatedBy: me }, where: `GuestId in (${dupIds.join(',')})` });
      toast(`Merged ${dups.length} into ${who}${numbers.length ? ` · other numbers kept in Remarks` : ''}`);
      setOpen(false);
      refresh();
    } catch {
      toast('Merge failed - check your connection');
      refresh();
    } finally {
      setMerging(false);
    }
  };

  async function saveFamilyMerged(payload, cardText) {
    const addedByName = newOwner.name;
    const skipped = [];
    let moved = 0;
    let created = 0;
    // ── the head ──
    let headId;
    const exHead = findExisting(form.MobileNumber, form.Surname, form.GuestName);
    if (exHead) {
      headId = Number(exHead.GuestId);
      const famNow = Number(exHead.FamilyId || 0);
      if (famNow && famNow !== headId && headsOthers(exHead)) throw new Error('head-of-other');
      await UpdateGuest({
        // A guest made head of a family is the head: the order a household
        // prints in starts from that.
        data: { FamilyId: headId, Relation: RELATION_HEAD, ...cardText, UpdatedBy: me },
        where: `GuestId in (${guestIdsOf(exHead).join(',')})`,
      });
      await SetGuestFunctions({
        GuestId: headId, EventId: event.EventId, OrganiserId: organiser.OrganiserId,
        FunctionIds: [...new Set([...idsOfPerson(exHead), ...selectedFunctionIds])], UpdatedBy: me,
      });
    } else {
      const res = await AddGuestToFunctions({
        ...payload, ...cardText, Relation: RELATION_HEAD, FunctionIds: selectedFunctionIds, IsFamilyHead: 1, FamilyMembers: [],
        EventId: event.EventId, OrganiserId: organiser.OrganiserId,
        AddedByName: addedByName, AddedByMobile: newOwner.mobile, Valid: 1, CreatedBy: newOwner.id, UpdatedBy: me,
      });
      headId = Number(res?.insertId);
      if (!headId) throw new Error('no-head');
    }
    // ── the members ──
    const filled = party.filter(m => String(m.GuestName || '').trim() || String(m.MobileNumber || '').trim());
    for (const m of filled) {
      const memberFns = partyFunctionIds(m);
      const ex = findExisting(m.MobileNumber, m.Surname || form.Surname, m.GuestName, headId);
      const label = [m.Surname || form.Surname, m.GuestName].filter(Boolean).join(' ');
      try {
        if (ex) {
          if (headsOthers(ex)) { skipped.push(`${label} (head of another family)`); continue; }
          if (Number(ex.FamilyId || 0) && Number(ex.FamilyId) !== headId) {
            // Out of the old family first - the move only takes free guests.
            await UpdateGuest({ data: { FamilyId: 0, UpdatedBy: me }, where: `GuestId in (${guestIdsOf(ex).join(',')})` });
          }
          await AddOrMoveFamilyMember({
            HeadGuestId: headId, ExistingGuestId: Number(ex.GuestId), EventId: event.EventId,
            OrganiserId: organiser.OrganiserId, CreatedBy: newOwner.id, UpdatedBy: me,
          });
          // Who they are in THIS household - the move itself only changes the
          // family, so a relation typed on the line has to be written.
          if (m.Relation) {
            await UpdateGuest({
              data: { Relation: m.Relation, UpdatedBy: me },
              where: `GuestId in (${guestIdsOf(ex).join(',')})`,
            });
          }
          await SetGuestFunctions({
            GuestId: Number(ex.GuestId), EventId: event.EventId, OrganiserId: organiser.OrganiserId,
            FunctionIds: [...new Set([...idsOfPerson(ex), ...memberFns])], UpdatedBy: me,
          });
          moved += 1;
        } else {
          const res = await AddOrMoveFamilyMember({
            HeadGuestId: headId, EventId: event.EventId, OrganiserId: organiser.OrganiserId,
            FunctionIds: memberFns, Surname: m.Surname || form.Surname || null, GuestName: m.GuestName || null,
            MobileNumber: m.MobileNumber ? ten(m.MobileNumber) : null, Relation: m.Relation || null,
            Side: payload.Side, Visibility: payload.Visibility, AddedByName: addedByName,
            AddedByMobile: newOwner.mobile, CreatedBy: newOwner.id, UpdatedBy: me,
          });
          if (res?.action === 'moved' || res?.action === 'already-member') moved += 1; else created += 1;
        }
      } catch (err) {
        skipped.push(`${label} (${err?.response?.data?.error || 'could not save'})`);
      }
    }
    const headName = [form.Surname, form.GuestName].filter(Boolean).join(' ');
    const parts = [`${headName}'s family saved${exHead ? ' (existing guest made head)' : ''}`];
    if (moved) parts.push(`${moved} moved in`);
    if (created) parts.push(`${created} added`);
    if (skipped.length) parts.push(`not moved: ${skipped.join(', ')}`);
    return { message: parts.join(' · ') };
  }

  // save({ addNext:true }) keeps the form open for the next guest: functions,
  // side and visibility stay, name/mobile clear, the list refreshes behind.
  const save = async ({ addNext = false } = {}) => {
    if (!selectedFunctionIds.length) return toast('Pick at least one function');
    if (!String(form.GuestName).trim()) return toast('Name is required');
    // A phone number is OPTIONAL: many relatives are invited in person or by
    // card. When one IS given it must be a real 10-digit mobile, because that
    // is what the WhatsApp invite and the duplicate check work on.
    const headMobile = String(form.MobileNumber || '').trim();
    if (headMobile && !/^\d{10}$/.test(headMobile)) return toast('A mobile number must be 10 digits');
    // The same name for the same function is a repeat, not a new guest.
    // (Add Family merges existing people instead - see saveFamilyMerged.)
    if (!editing && !familyMode) {
      const sameName = clashingFunctions(nameMatches(form.Surname, form.GuestName));
      if (sameName.length) {
        return toast(`Already saved - ${[form.Surname, form.GuestName].filter(Boolean).join(' ')} is invited to ${sameName[0].FunctionName || fnName(sameName[0].FunctionId)} (added by ${whoAdded(sameName[0])})`);
      }
      // Two members of one family may share a name; two of them may not share
      // a number, so the family rows are checked against each other too.
      const dupWithin = party.filter(m => String(m.GuestName || '').trim())
        .find((m, i, all) => all.findIndex(x => lc(x.GuestName) === lc(m.GuestName)) !== i);
      if (dupWithin) return toast(`${dupWithin.GuestName} is listed twice in this family`);
    }
    if (familyMode) {
      // A family may be saved with the HEAD ALONE - members are often added
      // later, from the family's own screen. Only rows that were half-filled
      // are refused: a name is what makes a member.
      const filled = party.filter(m => String(m.GuestName || '').trim() || String(m.MobileNumber || '').trim());
      if (filled.some(m => !String(m.GuestName || '').trim())) {
        return toast('Give a name for every family member, or clear the empty row');
      }
      const badMobile = party.find(m => String(m.MobileNumber || '').trim() && !/^\d{10}$/.test(String(m.MobileNumber).trim()));
      if (badMobile) return toast('A family member\'s mobile number must be 10 digits');
    }

    setSaving(true);
    try {
      // One-click save: dedup-check inline. Block only a true duplicate (the same
      // mobile already in MY list for THIS function); otherwise save right away.
      let dupeRows = dupes;
      if (!editing && !familyMode && headMobile) {
        dupeRows = await checkMobile(form.MobileNumber);
        // Whoever added it: one number belongs to one person, so the same
        // number for the same function is the same invitation twice.
        const blocked = dupeRows.find(d => rowFunctionIds(d).some(id => selectedFunctionIds.includes(String(id))));
        if (blocked) {
          setSaving(false);
          return toast(`Already saved - this number is invited to ${blocked.FunctionName || fnName(blocked.FunctionId)} (added by ${whoAdded(blocked)})`);
        }
      }
      const dupExists = !editing && dupeRows.length > 0;
      // eslint-disable-next-line no-unused-vars
      // FamilyNames is the UI's shorthand for the rest of the household; like
      // the Card* keys it is NOT a column, so it never reaches the payload.
      const { FunctionIds, ExtraCards, CardMode, CardFamilyId, CardHeadText, CardName, CardNameAuto, CardDeliveredByName, FamilyNames, ...singleForm } = form;
      // The household's card (Cards page fields). On ANOTHER head's card the
      // card text belongs to that head, so nothing of it is written here.
      // "On the card of …": a typed name that matches exactly one head is
      // accepted; otherwise the save is refused - never silently "own card".
      let resolvedHead = CardFamilyId;
      if (!familyMode && CardMode === 'family' && !resolvedHead) {
        const typed = lc(CardHeadText).split('·')[0].trim();
        const hits = typed ? cardHeadOptions.filter(o => lc(o.label).split('·')[0].trim() === typed
          || lc([o.head.Surname, o.head.GuestName].filter(Boolean).join(' ')) === typed) : [];
        if (hits.length === 1) resolvedHead = hits[0].value;
        else {
          setSaving(false);
          return toast(hits.length > 1
            ? `${hits.length} guests are called "${CardHeadText}" - pick the right one from the list (the mobile number tells them apart)`
            : 'Pick whose card this guest is on from the list, or choose Own card');
        }
      }
      const onOtherCard = !familyMode && CardMode === 'family' && !!resolvedHead;
      const cardText = onOtherCard ? {} : {
        CardName: CardNameAuto ? null : (String(CardName || '').trim() || null),
        CardDeliveredByName: String(CardDeliveredByName || '').trim() || null,
        ...(Number(ExtraCards) > 0 ? { ExtraCards: Math.min(99, Number(ExtraCards)) } : (editing ? { ExtraCards: 0 } : {})),
      };
      const target = onOtherCard ? cardHeadOptions.find(f => f.value === String(resolvedHead)) : null;
      if (onOtherCard && !target) { setSaving(false); return toast('Pick whose card this guest is on'); }
      if (onOtherCard && editing && target.guestIds && target.guestIds.map(String).includes(String(editing.GuestId))) {
        setSaving(false); return toast('A guest cannot be on their own card - choose Own card');
      }
      const editFam = editing ? Number(editing.FamilyId || 0) : 0;
      const editIsHead = !!editing && editFam && editFam === Number(editing.GuestId);
      const editIds = editing ? (editing._guestIds?.length ? editing._guestIds : [editing.GuestId]) : [];
      if (onOtherCard && editing && String(editFam) !== String(CardFamilyId)) {
        const headOfOthers = editIsHead && rows.some(x => Number(x.FamilyId || 0) === editFam && Number(x.GuestId) !== Number(editing.GuestId));
        if (headOfOthers) { setSaving(false); return toast('This guest is the head of a family card - move the family members first'); }
      }
      if (onOtherCard && target.size >= MAX_FAMILY_SIZE && !(editing && String(editFam) === String(resolvedHead))) {
        setSaving(false); return toast(`That card already has ${target.size} people (max ${MAX_FAMILY_SIZE})`);
      }
      // The family id this guest will carry; an individual chosen as head
      // becomes the head of a new family (their FamilyId = their GuestId).
      let joinFamilyId = onOtherCard ? Number(String(resolvedHead).replace(/^g:/, '')) : 0;
      if (onOtherCard && String(resolvedHead).startsWith('g:')) {
        await UpdateGuest({ data: { FamilyId: joinFamilyId, UpdatedBy: me }, where: `GuestId in (${target.guestIds.map(Number).join(',')})` });
      }
      // lockedSide wins over whatever the form holds: a member can only ever
      // create guests for their own side (admins are not locked).
      // The GROUP is the only thing asked; GuestType is derived from it, so
      // the card rules (no card for the own family, the firm on a business
      // card) keep working without a second question.
      const payload = emptyToNull({
        ...singleForm,
        GuestType: editing && isCountGroup(editing)
          ? COUNT_GROUP_TYPE                      // never demote a count-only row
          : guestTypeForGroup(singleForm.GroupName, singleForm.CompanyName),
        Side: isMarriage ? (lockedSide || singleForm.Side) : 'Common',
        ExpectedCount: 1,
      });
      if (editing) {
        await UpdateGuest({ data: { ...payload, UpdatedBy: me }, where: `GuestId = ${editing.GuestId}` });
        await SetGuestFunctions({
          GuestId: editing.GuestId,
          EventId: event.EventId,
          OrganiserId: organiser.OrganiserId,
          FunctionIds: selectedFunctionIds,
          UpdatedBy: me,
        });
        // Whose card: join another head's card, or back to an own card.
        const famData = onOtherCard
          ? (editFam !== joinFamilyId ? { FamilyId: joinFamilyId } : {})
          : (editFam && !editIsHead ? { FamilyId: 0 } : {});
        if (Object.keys(famData).length) {
          await UpdateGuest({ data: { ...famData, UpdatedBy: me }, where: `GuestId in (${editIds.map(Number).join(',')})` });
        }
        // Own card text: a family head's card text goes to the whole family.
        if (!onOtherCard) {
          await UpdateGuest({
            data: { ...cardText, UpdatedBy: me },
            where: editIsHead ? `FamilyId = ${Number(editFam)} and EventId = ${Number(event.EventId)}` : `GuestId in (${editIds.map(Number).join(',')})`,
          });
        }
        toast('Updated');
      } else if (familyMode) {
        const r = await saveFamilyMerged(payload, cardText);
        toast(r.message);
      } else {
        const addedByName = newOwner.name || '';
        const familyMembers = familyMode
          ? party.filter(m => String(m.GuestName || '').trim() || String(m.Surname || '').trim())
          // Family members are asked for a name only - they carry the head's
          // surname unless one was typed for them.
          .map(m => ({
            Surname: m.Surname || form.Surname || null,
            GuestName: m.GuestName || null,
            MobileNumber: m.MobileNumber ? ten(m.MobileNumber) : null,
            // Who they are in the household - it decides where they print.
            Relation: m.Relation || null,
            // Only the functions this person is invited to (empty = the head's).
            FunctionIds: partyFunctionIds(m),
          }))
          // One guest can bring their household with them: "Others in the
          // family" on the Add Guest form, the same plain names Quick add
          // takes, without switching to the family dialog.
          : parseFamilyNames(form.FamilyNames, form.Surname);
        const res = await AddGuestToFunctions({
          ...payload,
          ...cardText,
          FunctionIds: selectedFunctionIds,
          // Saved as a family even with no members yet: the head becomes the
          // family, and the rest of the household is added on the Families
          // screen later. One family = one invitation card.
          Relation: (familyMode || familyMembers.length) ? RELATION_HEAD : (payload.Relation || null),
          IsFamilyHead: (familyMode || familyMembers.length) ? 1 : 0,
          FamilyMembers: familyMembers,
          EventId: event.EventId,
          OrganiserId: organiser.OrganiserId,
          AddedByName: addedByName || null,
          AddedByMobile: newOwner.mobile,
          Valid: 1,
          CreatedBy: newOwner.id,
          UpdatedBy: me,
        });
        // "Extra members" - people on this card whose names are not taken (a
        // wife, an elderly mother). They are not guests of their own: the head
        // simply counts as more persons, which is what the headcount and the
        // caterer read. The add call always writes 1, so the count goes on
        // afterwards, the same way a count-only group does it.
        const persons = Math.max(1, Number(form.ExpectedCount || 1));
        if (persons > 1) {
          const headId = res?.insertId || (res?.insertedIds || [])[0];
          if (headId) {
            await UpdateGuest({
              data: { ExpectedCount: persons, UpdatedBy: me },
              where: `GuestId = ${Number(headId)} and EventId = ${Number(event.EventId)}`,
            });
          }
        }
        // On another head's card: the new guest joins that family.
        if (onOtherCard) {
          const newIds = (Array.isArray(res?.insertedIds) && res.insertedIds.length ? res.insertedIds : [res?.insertId]).filter(Boolean);
          if (newIds.length) {
            await UpdateGuest({ data: { FamilyId: joinFamilyId, UpdatedBy: me }, where: `GuestId in (${newIds.map(Number).join(',')})` });
          }
        }
        const familyRows = res?.familyRows || 1;
        toast(familyMode || familyMembers.length
          ? `Family of ${familyRows} added`
          : (dupExists ? 'Guest added to selected functions' : 'Guest added'));
      }
      // Remember what was used so the next guest starts from the same choices.
      writeDefaults(event.EventId, { FunctionIds: selectedFunctionIds, Side: form.Side });
      if (addNext && !editing) {
        setAddedThisSession(n => n + 1);
        // Keep what does not change between two people of one house: the
        // functions, the side, the visibility AND the surname. Only the name
        // and the number are typed again.
        setForm(f => ({
          ...blankForm(),
          FunctionIds: f.FunctionIds, FunctionId: f.FunctionId,
          Side: f.Side, Surname: f.Surname,   // Visibility and the person count start over
        }));
        setParty(familyMode ? blankMembers() : []);
        // The set just saved is kept exactly, so "auto off" has to be kept too.
        setWeddingOff(openAutoFunctionIds(functions).some(id => !selectedFunctionIds.includes(id)));
        setAddMode('new'); setDupes([]); setCheckedMobile('');
        refresh();
        return;
      }
      setOpen(false); refresh();
    } catch (err) {
      toast(err?.message === 'head-of-other'
        ? 'This head is already head of another family - open that family instead'
        : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Functions ticked straight in the guest table (desktop) ───────────────
  // One checkbox per function on each row; ticking saves at once for that
  // guest (SetEvt_GuestFunctions), with the Wedding rule. A guest saved
  // without the Wedding had it removed on purpose, so it is not re-added.
  const [savingFn, setSavingFn] = useState('');   // `${GuestId}:${FunctionId}` being saved
  const rowHasFunction = (row, functionId) => rowFunctionIds(row).map(String).includes(String(functionId))
    || (row._functionIds || []).map(String).includes(String(functionId));
  const toggleRowFunction = async (row, functionId) => {
    if (!(mine(row) && canEdit) || savingFn) return;
    const fn = functions.find(f => String(f.FunctionId) === String(functionId));
    if (!fn || isPastFunction(fn)) return;
    const current = [...new Set([...rowFunctionIds(row), ...(row._functionIds || [])].map(String))];
    const hadWedding = current.some(id => isWeddingFunction(functions.find(f => String(f.FunctionId) === id)));
    const res = toggleWithWedding(current, functionId, functions, current.length > 0 && !hadWedding);
    if (!res.ids.length) { toast('A guest needs at least one function - delete the guest instead'); return; }
    const names = functions.filter(f => res.ids.includes(String(f.FunctionId))).map(f => f.FunctionName).join(', ');
    const ids = (row._guestIds && row._guestIds.length ? row._guestIds : [row.GuestId]).map(String);
    // Show it at once; the list reloads quietly afterwards.
    setRows(rs => rs.map(x => (ids.includes(String(x.GuestId)) ? { ...x, FunctionIds: res.ids.join(','), FunctionNames: names, FunctionName: names } : x)));
    setSavingFn(`${row.GuestId}:${functionId}`);
    try {
      await SetGuestFunctions({
        GuestId: row.GuestId, EventId: event.EventId, OrganiserId: organiser.OrganiserId,
        FunctionIds: res.ids, UpdatedBy: me,
      });
    } catch {
      toast('Could not save - check your connection');
    } finally {
      setSavingFn('');
      refresh(true);
    }
  };

  // Public / Private checkbox column: one guest, or every guest on screen at
  // once (header box - pick "Added by: Haresh" first to mark only his).
  // Public = Visibility 'Common' (all members see it), else 'Private'.
  // Only guests this user may edit are changed.
  // Guest Type column (Relative / Friend / Business): saved at once. The firm
  // of a business guest is still typed in the guest form.
  // The guest list's own picker: one group, and the type follows.
  const setGroupForRow = async (row, group) => {
    if (!(mine(row) && canEdit)) return;
    const ids = (row._guestIds && row._guestIds.length ? row._guestIds : [row.GuestId]).map(Number).filter(Boolean);
    if (!ids.length) return;
    const value = String(group || '').trim();
    // A count-only group ("Gym Friends · 9") is a KIND of row, not a circle of
    // people: changing which group it belongs to must not turn it into an
    // ordinary guest, or it loses its 👥 count and its own form.
    const type = isCountGroup(row) ? COUNT_GROUP_TYPE : guestTypeForGroup(value, row.CompanyName);
    const idSet = new Set(ids.map(String));
    setRows(rs => rs.map(x => (idSet.has(String(x.GuestId)) ? { ...x, GroupName: value || null, GuestType: type } : x)));
    try {
      await UpdateGuest({
        data: { GroupName: value || null, GuestType: type, UpdatedBy: me },
        where: `GuestId in (${ids.join(',')}) and EventId = ${Number(event.EventId)}`,
      });
    } catch {
      toast('Could not save - check your connection');
    } finally {
      refresh(true);
    }
  };

  const setGuestTypeFor = async (row, type) => {
    if (!(mine(row) && canEdit)) return;
    const ids = (row._guestIds && row._guestIds.length ? row._guestIds : [row.GuestId]).map(Number).filter(Boolean);
    if (!ids.length) return;
    const idSet = new Set(ids.map(String));
    setRows(rs => rs.map(x => (idSet.has(String(x.GuestId)) ? { ...x, GuestType: type } : x)));
    try {
      await UpdateGuest({ data: { GuestType: type, UpdatedBy: me }, where: `GuestId in (${ids.join(',')}) and EventId = ${Number(event.EventId)}` });
    } catch {
      toast('Could not save - check your connection');
    } finally {
      refresh(true);
    }
  };
  const [savingVis, setSavingVis] = useState(false);
  const isPublic = (row) => (row.Visibility || 'Private') === 'Common';
  const setVisibilityFor = async (list, makePublic) => {
    if (!canEdit || savingVis) return;
    const value = makePublic ? 'Common' : 'Private';
    const targets = (list || []).filter(r => mine(r) && isPublic(r) !== makePublic);
    const ids = [...new Set(targets.flatMap(r => (r._guestIds && r._guestIds.length ? r._guestIds : [r.GuestId])).map(Number).filter(Boolean))];
    if (!ids.length) return;
    const idSet = new Set(ids.map(String));
    setRows(rs => rs.map(x => (idSet.has(String(x.GuestId)) ? { ...x, Visibility: value } : x)));
    setSavingVis(true);
    try {
      await UpdateGuest({ data: { Visibility: value, UpdatedBy: me }, where: `GuestId in (${ids.join(',')}) and EventId = ${Number(event.EventId)}` });
      if (targets.length > 1) toast(`${targets.length} guests marked ${makePublic ? 'Public' : 'Private'}`);
    } catch {
      toast('Could not save - check your connection');
    } finally {
      setSavingVis(false);
      refresh(true);
    }
  };

  // ── Picking rows, and what can be done to all of them at once ────────────
  // The Public header box flips EVERY guest on screen; this is the other way
  // round - tick a few rows and act on just those: make them Private / Public,
  // or hand them over to another member ("Added by"). A family row carries its
  // members with it, exactly as the Public column does.
  const [picked, setPicked] = useState(() => new Set());
  const rowKey = (row) => String(row.GuestId);
  const isPicked = useCallback((row) => picked.has(String(row.GuestId)), [picked]);
  const togglePick = (row) => setPicked(prev => {
    const next = new Set(prev);
    const k = rowKey(row);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const pickRows = (list, on) => setPicked(prev => {
    const next = new Set(prev);
    (list || []).forEach(r => { if (on) next.add(rowKey(r)); else next.delete(rowKey(r)); });
    return next;
  });
  const clearPicked = () => setPicked(new Set());
  const pickedFrom = useCallback((list) => (list || []).filter(r => picked.has(rowKey(r))), [picked]);

  // Who a picked guest can be handed to: the team of this event, plus anyone
  // who already appears as an adder (a guest who invited people, a member who
  // has since left the team) - the same names the "Added by" filter shows.
  const addedByTargets = useMemo(() => {
    const byId = new Map();
    team.forEach(t => {
      const id = String(t.UserId || '');
      if (!id) return;
      byId.set(id, {
        value: id,
        // Surname first, the way every other place writes AddedByName - the
        // other order put one person in the data twice ("Ailani Sanjay" and
        // "Sanjay Ailani"), which split his card list in two.
        name: [t.Surname, t.Name].filter(Boolean).join(' ').trim() || `User ${id}`,
        mobile: String(t.MobileNumber || '').trim(),
      });
    });
    adderOptions.forEach(a => { if (!byId.has(a.value)) byId.set(a.value, { value: a.value, name: a.name, mobile: a.mobile }); });
    return [...byId.values()]
      .map(u => ({ ...u, label: `${u.name}${u.mobile ? ' - ' + u.mobile : ''}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [team, adderOptions]);

  const [savingGroup, setSavingGroup] = useState(false);
  // Move the picked guests into a group ("Electric Group", "Rotary" …), the
  // same way they are handed to another member. A group is a label on the
  // guest, so nothing else moves with it; an empty value clears it.
  const setGroupFor = async (list, group) => {
    if (!canEdit || savingGroup) return;
    const value = String(group || '').trim();
    const targets = (list || []).filter(r => mine(r) && String(r.GroupName || '').trim() !== value);
    const ids = [...new Set(targets.flatMap(r => (r._guestIds && r._guestIds.length ? r._guestIds : [r.GuestId])).map(Number).filter(Boolean))];
    if (!ids.length) { toast(value ? `Already in ${value}` : 'No group to clear'); return; }
    const idSet = new Set(ids.map(String));
    setRows(rs => rs.map(x => (idSet.has(String(x.GuestId)) ? { ...x, GroupName: value || null } : x)));
    setSavingGroup(true);
    try {
      await UpdateGuest({
        data: { GroupName: value || null, UpdatedBy: me },
        where: `GuestId in (${ids.join(',')}) and EventId = ${Number(event.EventId)}`,
      });
      toast(`${ids.length} ${ids.length === 1 ? 'guest' : 'guests'} ${value ? `moved to ${value}` : 'taken out of their group'}`);
      clearPicked();
    } catch {
      toast('Could not save - check your connection');
    } finally {
      setSavingGroup(false);
      refresh(true);
    }
  };

  const [savingOwner, setSavingOwner] = useState(false);
  // Hand guests over to another member. CreatedBy decides who may edit them and
  // whose line they count on in the Brief Report, so AddedByName / AddedByMobile
  // (the snapshot every list shows) move with it. Admins only - it is an
  // ownership transfer, and a Private guest moved this way leaves the old
  // adder's list for good.
  const setAddedByFor = async (list, userId) => {
    if (!isAdmin || savingOwner) return false;
    const to = addedByTargets.find(u => String(u.value) === String(userId));
    if (!to) return false;
    const targets = (list || []).filter(r => String(r.CreatedBy) !== String(to.value));
    const ids = [...new Set(targets.flatMap(r => (r._guestIds && r._guestIds.length ? r._guestIds : [r.GuestId])).map(Number).filter(Boolean))];
    if (!ids.length) { toast('Those guests are already added by ' + to.name); return false; }
    setSavingOwner(true);
    try {
      await UpdateGuest({
        data: {
          CreatedBy: Number(to.value), AddedByName: to.name,
          AddedByMobile: to.mobile || null, UpdatedBy: me,
        },
        where: `GuestId in (${ids.join(',')}) and EventId = ${Number(event.EventId)}`,
      });
      toast(`${ids.length} ${ids.length === 1 ? 'guest' : 'guests'} now added by ${to.name}`);
      clearPicked();
      return true;
    } catch {
      toast('Could not save - check your connection');
      return false;
    } finally {
      setSavingOwner(false);
      refresh(true);
    }
  };
  // The same hand-over from the Edit Guest form: the guest being edited (all
  // their function rows) goes to another member at once, and the form shows
  // the new name without being closed.
  const setEditingAddedBy = async (userId) => {
    if (!editing) return;
    const to = addedByTargets.find(u => String(u.value) === String(userId));
    if (!to || !(await setAddedByFor([editing], to.value))) return;
    setEditing(e => (e ? {
      ...e, CreatedBy: Number(to.value), AddedByName: to.name, InvitedByName: to.name,
      AddedByMobile: to.mobile || null, InvitedByMobile: to.mobile || null, AddedByGuest: 0,
    } : e));
  };


  // Card of a guest (table column): head of a family, on someone's card, or own.
  const cardInfo = useCallback((row) => {
    const famId = Number(row.FamilyId || 0);
    if (!famId) return { kind: 'own', extra: Number(row.ExtraCards || 0) };
    const fam = familyOptions.find(f => Number(f.familyId) === famId);
    if (famId === Number(row.GuestId)) return { kind: 'head', size: fam ? fam.size : 1, extra: Number(row.ExtraCards || 0) };
    const head = fam && fam.head;
    return { kind: 'member', headName: head ? [head.Surname, head.GuestName].filter(Boolean).join(' ') : 'family', extra: 0 };
  }, [familyOptions]);

  // What the Share / Export dialog prints for this list: the columns a guest
  // list is actually read for, over the rows the filters leave on screen - so
  // the file matches what the page shows, filter for filter. Remarks is left
  // out on purpose: it holds working notes (merged numbers and such), not
  // something to hand around on a printed list.
  // Groom / Bride in one phrase: the chosen filter, or what the rows actually
  // are when nothing is filtered.
  const sideLabel = useMemo(() => {
    if (!isMarriage) return '';
    if (sideFilter && sideFilter !== 'all') return `${sideFilter} side`;
    const kinds = [...new Set(filtered.map(r => sideKey(r.Side)).filter(k => k && k !== 'Common'))];
    if (kinds.length === 1) return `${kinds[0]} side`;
    return kinds.length > 1 ? 'Groom & Bride' : '';
  }, [isMarriage, sideFilter, filtered]);

  // Serial number of each exported line (1, 2, 3 … in list order), and one
  // ✓ column per function still to come - the same columns as the screen -
  // so the printout is checked and corrected function by function.
  const report = useMemo(() => {
    // The printed list is read to find a NAME ("is X on it?"), so it runs
    // Added by → Group → Surname → Name, A-Z, whatever the screen is sorted by
    // (owner, 2026-10-15). Each guest sorts under the person who added THEM -
    // not under their household's head, which scattered one adder's guests
    // between another's. A blank adder / group comes last.
    const txt = (v) => String(v || '').trim().toLowerCase();
    const everyone = !adder;
    const adderNameOf = new Map(adderOptions.map(o => [String(o.value), o.name]));
    const addedByOf = (r) => adderNameOf.get(String(r.CreatedBy || '')) || String(r.AddedByName || r.InvitedByName || '').trim();
    const shortAdders = adderShortNames(filtered.map(addedByOf));
    const blankLast = (a, b) => (a === b ? 0 : !a ? 1 : !b ? -1 : a.localeCompare(b));
    const sortedRows = [...filtered].sort((a, b) => blankLast(txt(addedByOf(a)), txt(addedByOf(b)))
      || blankLast(txt(a.GroupName), txt(b.GroupName))
      || blankLast(txt(a.Surname), txt(b.Surname))
      || txt(a.GuestName).localeCompare(txt(b.GuestName)));
    const serial = new Map(sortedRows.map((r, i) => [r, i + 1]));
    // One party chosen? Every row would carry the same tick, so the columns
    // go and the header names the party instead - date and time included.
    const reportFunctions = singleFunction ? [] : functions.filter(f => !isPastFunction(f));
    const has = (r, id) => rowFunctionIds(r).map(String).includes(String(id))
      || (r._functionIds || []).map(String).includes(String(id));
    // A tick says "this person is coming". A line that stands for nine people
    // says NINE instead - a tick there would be read as one plate.
    const fnCols = reportFunctions.map(f => ({
      key: `fn_${f.FunctionId}`, label: f.FunctionName, align: 'center', tick: true, keepEmpty: true,
      excelWidth: 10, pdfWidth: 15,
      exportValue: (r) => {
        if (!has(r, f.FunctionId)) return '';
        const n = Math.max(1, Number(r.ExpectedCount || 1));
        return n > 1 ? String(n) : '✓';
      },
    }));
    // A line is not always a person: "Gym Friends · 9" is one line and nine
    // guests. Every total on this sheet counts PEOPLE, because that is what
    // the caterer, the printer and whoever is calling them all work from.
    const persons = (list) => list.reduce((n, r) => n + Math.max(1, Number(r.ExpectedCount || 1)), 0);
    const totalPersons = persons(filtered);
    const totalsRow = {
      SNo: '', Surname: 'Total',
      GuestName: totalPersons === filtered.length
        ? `${filtered.length} guests`
        : `${filtered.length} lines · ${totalPersons} guests`,
      ...Object.fromEntries(reportFunctions.map(f => [`fn_${f.FunctionId}`,
        String(persons(filtered.filter(r => has(r, f.FunctionId))))])),
    };
    return {
    title: 'Guests',
    // Who 'A-H' is, said once under the heading.
    note: everyone && shortAdders.size ? `Added by — ${adderLegend(shortAdders)}` : undefined,
    // Said at the top of the sheet as well, where the eye lands first.
    countText: totalPersons === filtered.length
      ? `${filtered.length} guests`
      : `${filtered.length} lines · ${totalPersons} guests`,
    subtitle: [
      singleFunction ? `Party: ${partyLine}` : fnFilterLabel,
      // Said ONCE in the header, never as a column repeating down every page:
      // which side these guests are, and whose list it is.
      sideLabel,
      (guestTypeFilter === 'business' || listView === 'business') ? 'Business associates' : '',
      adder ? `Added by: ${adder.label}` : '',
      groupFilter === '__none' ? 'No group' : groupFilter ? `Group: ${groupFilter}` : '',
      q.trim() ? `search "${q.trim()}"` : '',
    ].filter(Boolean).join(' · '),
    columns: [
      // A serial number needs only the width of "300": '#' keeps the heading as narrow.
      { key: 'SNo', label: '#', align: 'num', pdfWidth: 6, excelWidth: 5, exportValue: (r) => serial.get(r) || '' },
      // Everyone's list says who added each guest - it is sorted by it. One
      // person's list names them once, in the header.
      // Printed SHORT - "A-H", not "Ailani Haresh" - because the column would
      // otherwise repeat one of four names down three hundred lines and take
      // the width from the guests' own names. The key is under the heading.
      ...(everyone ? [{ key: 'AddedBy', label: 'By', align: 'center', pdfWidth: 12, excelWidth: 8, nowrap: true,
        exportValue: (r) => shortAdders.get(addedByOf(r)) || adderInitials(addedByOf(r)) }] : []),
      // PDF widths in mm (scaled down by the builder when the page is narrower):
      // names get the room, a tick needs very little.
      // Every column has a share; the builder stretches / shrinks them all to
      // the page width. Mobile is ALWAYS printed - blank boxes are where the
      // missing numbers get written in, to be typed into the app afterwards.
      // Group first - the list is sorted by it. One group chosen? The header
      // already says it, so no column repeating it down the page.
      ...(groupFilter ? [] : [{ key: 'GroupName', label: 'Group', pdfWidth: 24, excelWidth: 16, exportValue: (r) => String(r.GroupName || '').trim() }]),
      { key: 'Surname', label: 'Surname', pdfWidth: 28, excelWidth: 16 },
      { key: 'GuestName', label: 'Name', pdfWidth: 32, excelWidth: 20,
        // Any line that stands for more than one person says so - a count-only
        // group, and a guest with "extra members" on their card.
        exportValue: (r) => {
          const n = Math.max(1, Number(r.ExpectedCount || 1));
          const name = r.GuestName || (isCountGroup(r) ? 'Group' : '');
          return n > 1 ? `${name} (${n} persons)` : name;
        } },
      { key: 'MobileNumber', label: 'Mobile Number', pdfWidth: 25, excelWidth: 12, keepEmpty: true },
      ...fnCols,
      // No Card column (owner, 2026-10-15): the card is the Cards page's and
      // the Brief's card list's business, not the guest list's.
    ],
    rows: sortedRows,
    totalsRow,
    };
  }, [filtered, functions, fnFilterLabel, singleFunction, partyLine, adder, adderOptions, groupFilter, sideLabel, guestTypeFilter, listView, q]);

  // ── Marking who someone is, and moving them in the family ────────────────
  // Both are done where the family is READ (the Families view), because that is
  // where you can see whether the order is right.
  const setRelationFor = async (row, relation) => {
    if (!(mine(row) && canEdit)) return;
    const ids = (row._guestIds && row._guestIds.length ? row._guestIds : [row.GuestId]).map(Number).filter(Boolean);
    if (!ids.length) return;
    const value = String(relation || '').trim();
    const idSet = new Set(ids.map(String));
    setRows(rs => rs.map(x => (idSet.has(String(x.GuestId)) ? { ...x, Relation: value || null } : x)));
    try {
      await UpdateGuest({
        data: { Relation: value || null, UpdatedBy: me },
        where: `GuestId in (${ids.join(',')}) and EventId = ${Number(event.EventId)}`,
      });
    } catch { toast('Could not save - check your connection'); refresh(true); }
  };

  // Up / down inside one household. An order only means anything as a whole,
  // so every member's number is rewritten in one call.
  const [movingMember, setMovingMember] = useState(null);
  const moveFamilyMember = async (head, row, dir) => {
    if (!canEdit || movingMember) return;
    const famId = Number(head?.FamilyId || head?.GuestId || 0);
    if (!famId) return;
    const members = rows.filter(r => Number(r.FamilyId || 0) === famId && String(r.GuestId) !== String(famId));
    const saved = moveMember(members, row.GuestId, dir);
    if (!saved) return;                       // already at the top / bottom
    const order = new Map(saved.map(x => [String(x.GuestId), x.FamilyOrder]));
    setRows(rs => rs.map(x => (order.has(String(x.GuestId)) ? { ...x, FamilyOrder: order.get(String(x.GuestId)) } : x)));
    setMovingMember(String(row.GuestId));
    try {
      // One call per member: the numbers differ, so they cannot share a where.
      await Promise.all(saved.map(x => UpdateGuest({
        data: { FamilyOrder: x.FamilyOrder, UpdatedBy: me },
        where: `GuestId = ${Number(x.GuestId)} and EventId = ${Number(event.EventId)}`,
      })));
    } catch {
      toast('Could not save the order - check your connection');
      refresh(true);
    } finally {
      setMovingMember(null);
    }
  };

  // ── The list a member checks their own guests on ─────────────────────────
  // A flat A–Z table scatters a household: "Dhankani Son" sits nowhere near
  // "Dhankani Prakash", and nobody can tell who is missing. This prints ONE
  // BLOCK PER HOUSEHOLD - the head with his number, the functions and the
  // person count, then each member underneath in the order a family is read
  // (wife, son, his wife, their children, daughter, her husband, the elders),
  // never alphabetically. A guest on their own is simply one line.
  // Members' numbers are left off on purpose (owner's decision): the head's
  // number is the one anybody rings.
  const verifyReport = useMemo(() => {
    const txt = (v) => String(v || '').trim().toLowerCase();
    const open = functions.filter(f => !isPastFunction(f));
    const has = (r, id) => rowFunctionIds(r).map(String).includes(String(id))
      || (r._functionIds || []).map(String).includes(String(id));

    // One entry per household (family, else the person on their own).
    const byKey = new Map();
    filtered.forEach(r => {
      const famId = Number(r.FamilyId || 0);
      const key = famId ? `fam:${famId}` : `g:${r.GuestId}`;
      if (!byKey.has(key)) byKey.set(key, { key, famId, head: null, members: [] });
      const h = byKey.get(key);
      if (famId && String(r.GuestId) === String(famId)) h.head = r; else h.members.push(r);
    });
    // A family whose head is filtered out is led by its first listed member,
    // so nobody disappears from the sheet.
    byKey.forEach(h => { if (!h.head) { h.head = h.members.shift(); } });

    const households = [...byKey.values()].map(h => ({
      ...h,
      members: sortFamilyMembers(h.members),
      persons: [h.head, ...h.members].reduce((n, r) => n + Math.max(1, Number(r?.ExpectedCount || 1)), 0),
    }));

    // The SCREEN's sort decides the order of the households (by their head),
    // so changing the sort up there changes the printed order too. On the FULL
    // list - everybody's guests - the households are grouped by whoever invited
    // them first, so one member's families sit together and the sheet can be
    // torn up and handed round.
    const key = SORT_KEYS[sort.key] || SORT_KEYS.name;
    const whoAddedIt = (r) => txt(r?.AddedByName || r?.InvitedByName);
    households.sort((a, b) => {
      if (!adder) {
        const aw = whoAddedIt(a.head); const bw = whoAddedIt(b.head);
        if (aw !== bw) return (!aw - !bw) || aw.localeCompare(bw);   // nobody's last
      }
      const av = key(a.head); const bv = key(b.head);
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''));
      return sort.dir === 'desc' ? -cmp : cmp;
    });

    // Head line, then its members - flattened for the table, each row marked
    // so the builder can print a head in bold.
    const rows = [];
    households.forEach((h, i) => {
      rows.push({ ...h.head, _head: true, _no: i + 1, _persons: h.persons, _size: 1 + h.members.length });
      h.members.forEach(m => rows.push({ ...m, _head: false }));
    });

    const adderOfRow = (r) => String(r.AddedByName || r.InvitedByName || '').trim();
    const shortChecked = adderShortNames(households.map(h => adderOfRow(h.head || {})));
    const h0 = new Map(households.map(h => [Number(h.famId || 0), String(h.head?.Surname || '')]));
    const totalPersons = households.reduce((n, h) => n + h.persons, 0);
    return {
      title: 'Guest list to check',
      subtitle: [adder ? `Added by: ${adder.label}` : '', groupFilter ? `Group: ${groupFilter}` : '', fnFilterLabel]
        .filter(Boolean).join(' · '),
      note: (adder
        ? 'One block per household — please add anyone who is missing and cross out anyone who should not be there.'
        : 'One block per household, grouped by whoever invited them — please add anyone who is missing and cross out anyone who should not be there.')
        + (!adder && shortChecked.size ? '\nAdded by — ' + adderLegend(shortChecked) : ''),
      countText: `${households.length} households · ${totalPersons} guests`,
      isGroupHead: (r) => !!r._head,
      columns: [
        { key: '_no', label: '#', align: 'num', pdfWidth: 8, excelWidth: 5,
          exportValue: (r) => (r._head ? String(r._no) : '') },
        { key: 'Relation', label: 'Who', pdfWidth: 26, excelWidth: 16,
          exportValue: (r) => (r._head ? 'Head' : (relationLabel(r.Relation).split(' (')[0] || '—')) },
        { key: 'Name', label: 'Name', pdfWidth: 50, excelWidth: 26, exportValue: (r) => {
          const n = Math.max(1, Number(r.ExpectedCount || 1));
          // The head carries the surname; a member only repeats it when it is
          // a DIFFERENT one (a married daughter, a son-in-law).
          const headSurname = txt(h0.get(r.FamilyId) || '');
          const name = r._head
            ? [r.Surname, r.GuestName, r.FatherHusband].filter(Boolean).join(' ')
            : [r.GuestName, txt(r.Surname) && txt(r.Surname) !== headSurname ? r.Surname : ''].filter(Boolean).join(' ');
          return n > 1 ? `${name} (${n} persons)` : name;
        } },
        // Only the head's number: it is the one anybody rings.
        { key: 'MobileNumber', label: 'Mobile', pdfWidth: 26, excelWidth: 14, keepEmpty: true,
          exportValue: (r) => (r._head ? String(r.MobileNumber || '') : '') },
        // Whose guest this household is - a column only on the FULL list; when
        // one person is chosen the header already names them.
        ...(adder ? [] : [{ key: 'AddedByName', label: 'By', align: 'center', pdfWidth: 12, excelWidth: 8, nowrap: true,
          exportValue: (r) => (r._head ? (shortChecked.get(adderOfRow(r)) || adderInitials(adderOfRow(r))) : '') }]),
        ...open.map(f => ({
          key: `fn_${f.FunctionId}`, label: f.FunctionName, align: 'center', tick: true, keepEmpty: true,
          excelWidth: 10, pdfWidth: 14,
          exportValue: (r) => {
            if (!has(r, f.FunctionId)) return '';
            const n = Math.max(1, Number(r.ExpectedCount || 1));
            return n > 1 ? String(n) : '✓';
          },
        })),
        { key: '_persons', label: 'In all', align: 'num', pdfWidth: 12,
          exportValue: (r) => (r._head && r._size > 1 ? String(r._persons) : '') },
      ],
      rows,
      totalsRow: {
        _no: '', Relation: 'Total', Name: `${households.length} households · ${totalPersons} guests`,
        ...Object.fromEntries(open.map(f => [`fn_${f.FunctionId}`,
          String(filtered.filter(r => has(r, f.FunctionId)).reduce((n, r) => n + Math.max(1, Number(r.ExpectedCount || 1)), 0))])),
      },
    };
  }, [filtered, functions, sort, adder, groupFilter, fnFilterLabel]);

  // ── One list per SET of functions ───────────────────────────────────────
  // "Who is coming to what" is not one list but several: the people invited to
  // the Reception only, the ones invited to the Wedding AND the Reception, the
  // ones with the Thread Ceremony too. Guests are grouped by the EXACT set of
  // functions they are invited to, each set becomes its own list, and the
  // export puts every list on a sheet of its own (one PDF, or one Excel sheet
  // per set). The page's own filters still apply, so "Added by: X" or one
  // group narrows every list at once - the file says so in its header.
  const functionSets = useMemo(() => buildFunctionSets(
    combineGuestRows(filtered),
    functions.filter(f => !isPastFunction(f)),
    (g) => [...(g._functionIds || []), ...rowFunctionIds(g)],
  ), [filtered, functions]);

  // Which sets go into the file (all of them until one is unticked).
  const [setsOff, setSetsOff] = useState(() => new Set());
  const toggleSet = (key) => setSetsOff(prev => {
    const next = new Set(prev);
    if (next.has(String(key))) next.delete(String(key)); else next.add(String(key));
    return next;
  });
  const setsAllOn = functionSets.every(s => !setsOff.has(s.key));
  const toggleAllSets = () => setSetsOff(setsAllOn ? new Set(functionSets.map(s => s.key)) : new Set());
  const chosenSets = useMemo(() => functionSets.filter(s => !setsOff.has(s.key)), [functionSets, setsOff]);

  // The same five columns the printed guest list uses - which functions this
  // list is for is said once, in the heading, not repeated down every page.
  const setColumns = useMemo(() => [
    { key: 'SNo', label: '#', align: 'num', pdfWidth: 6, excelWidth: 5 },
    { key: 'Surname', label: 'Surname', pdfWidth: 30, excelWidth: 16 },
    { key: 'GuestName', label: 'Name', pdfWidth: 34, excelWidth: 20,
      exportValue: (r) => (isCountGroup(r) ? `${r.GuestName || 'Group'} (${Number(r.ExpectedCount || 1)} persons)` : (r.GuestName || '')) },
    { key: 'MobileNumber', label: 'Mobile Number', pdfWidth: 25, excelWidth: 12, keepEmpty: true },
    { key: 'City', label: 'City', pdfWidth: 20, excelWidth: 14 },
    { key: 'Card', label: 'Card', pdfWidth: 22, excelWidth: 16, exportValue: (r) => {
      if (String(r.GuestType || '') === 'Home') return 'No card (own family)';
      if (String(r.CardStatus || '') === NO_CARD_STATUS) return 'No card';
      const info = cardInfo(r);
      if (info.kind === 'head') return `Head · ${info.size}${info.extra ? ` (+${info.extra})` : ''}`;
      if (info.kind === 'member') return `On ${info.headName}`;
      return info.extra ? `Own (+${info.extra})` : 'Own';
    } },
  ], [cardInfo]);

  const setsReport = useMemo(() => {
    const txt = (v) => String(v || '').trim().toLowerCase();
    const sections = chosenSets.map(set => {
      const sorted = [...set.rows].sort((a, b) =>
        txt(a.Surname).localeCompare(txt(b.Surname)) || txt(a.GuestName).localeCompare(txt(b.GuestName)));
      const serial = new Map(sorted.map((r, i) => [r, i + 1]));
      return {
        title: set.label,
        note: `${set.guests} ${set.guests === 1 ? 'guest' : 'guests'} · ${set.persons} persons · invited to ${set.count === 1 ? 'this function only' : `these ${set.count} functions only`}`,
        newPage: true,                       // each list prints as its own list
        columns: setColumns.map(c => (c.key === 'SNo'
          ? { ...c, exportValue: (r) => String(serial.get(r) || '') } : c)),
        rows: sorted,
      };
    });
    return {
      title: 'Guest lists by function',
      subtitle: [event?.EventName, adder ? `Added by: ${adder.label}` : '', groupFilter ? `Group: ${groupFilter}` : '']
        .filter(Boolean).join(' · '),
      summary: [
        { label: 'Lists', value: chosenSets.length },
        { label: 'Guests', value: chosenSets.reduce((n, s) => n + s.guests, 0) },
        { label: 'Persons', value: chosenSets.reduce((n, s) => n + s.persons, 0) },
      ],
      sections,
    };
  }, [chosenSets, setColumns, event, adder, groupFilter]);

  const mark = async (row, status) => {
    const ids = row._guestIds?.length ? row._guestIds : [row.GuestId];
    try {
      await Promise.all(ids.map(id => UpdateGuest({ data: { InviteStatus: status, UpdatedBy: me }, where: `GuestId = ${id}` })));
      setRows(rs => rs.map(r => ids.includes(r.GuestId) ? { ...r, InviteStatus: status } : r));
    } catch { toast('Could not update status'); }
  };

  const del = async (row) => {
    const ids = row._guestIds?.length ? row._guestIds : [row.GuestId];
    const suffix = ids.length > 1 ? ` from ${ids.length} functions` : '';
    if (!window.confirm(`Remove ${row.GuestName || 'this member'}${suffix} from the list?`)) return;
    try {
      await Promise.all(ids.map(id => UpdateGuest({ data: { Valid: 0, UpdatedBy: me }, where: `GuestId = ${id}` })));
      toast('Removed'); refresh();
    } catch { toast('Remove failed'); }
  };

  // A member the family ALREADY has, taken off the Add Family grid. Two quite
  // different things, so they are two buttons and each says which it is:
  //   'family' - they stay a guest of the event, just not of this household
  //              (a son who has his own card now).
  //   'guest'  - they were never meant to be invited at all.
  const removeExistingMember = async (m, mode = 'family') => {
    if (!mine(m)) { toast('Only the member who added this guest can change it'); return; }
    const who = [m.Surname, m.GuestName].filter(Boolean).join(' ') || 'this person';
    const ids = guestIdsOf(m);
    if (headsOthers(m)) { toast(`${who} heads a family of their own - open that family instead`); return; }
    const ask = mode === 'guest'
      ? `Remove ${who} from the guest list altogether?`
      : `Take ${who} out of this family?
They stay a guest of the event, on their own card.`;
    if (!window.confirm(ask)) return;
    setSavingExisting(String(m.GuestId));
    try {
      await UpdateGuest({
        data: mode === 'guest' ? { Valid: 0, UpdatedBy: me } : { FamilyId: 0, UpdatedBy: me },
        where: `GuestId in (${ids.join(',')})`,
      });
      toast(mode === 'guest' ? 'Removed from the list' : 'Out of the family');
      await refresh(true);
    } catch {
      toast('Could not save - check your connection');
      refresh(true);
    } finally {
      setSavingExisting(null);
    }
  };

  const guestHasAllFunctions = (row) => {
    if (!functions.length) return false;
    const ids = rowFunctionIds(row).map(String);
    return functions.every(f => ids.includes(String(f.FunctionId)));
  };

  const openInvite = (row) => {
    setInviteRow(row);
  };
  const closeInvite = () => {
    if (inviteSharing) return;
    setInviteRow(null);
  };
  const sendInvite = async (kind) => {
    if (!inviteRow) return;
    // A guest may be saved without a number (invited in person or by card) -
    // there is then nowhere to send a WhatsApp invite.
    if (!String(inviteRow.MobileNumber || '').replace(/\D/g, '')) {
      toast('This guest has no mobile number - add one to send a WhatsApp invite');
      return;
    }
    if (kind === 'pdf' && !guestHasAllFunctions(inviteRow)) {
      toast('PDF invite is only for guests invited to all functions');
      return;
    }
    setInviteSharing(kind);
    try {
      await shareInvite(kind, inviteRow, event, organiser);
      await mark(inviteRow, 'Invited');
      setInviteCooldown(INVITE_COOLDOWN_SECONDS);
      toast('Invite sent');
    } catch (err) {
      toast(err?.message || 'WhatsApp invite failed');
    } finally {
      setInviteSharing('');
    }
  };

  const addOrMoveFamilyMember = async (head, member) => {
    const headName = [head.Surname, head.GuestName].filter(Boolean).join(' ').trim() || 'the family head';
    try {
      const result = await AddOrMoveFamilyMember({
        HeadGuestId: head.GuestId,
        ExistingGuestId: member.ExistingGuestId || null,
        EventId: event.EventId,
        OrganiserId: organiser.OrganiserId,
        FunctionIds: rowFunctionIds(head),
        Surname: member.Surname || null,
        GuestName: member.GuestName || null,
        MobileNumber: member.MobileNumber || null,
        Side: member.Side || head.Side,
        Visibility: member.Visibility || head.Visibility,
        Remarks: member.Remarks || null,
        AddedByName: newOwner.name,
        AddedByMobile: newOwner.mobile,
        CreatedBy: newOwner.id,
        UpdatedBy: me,
      });
      const memberName = [result?.Surname, result?.GuestName].filter(Boolean).join(' ').trim() || 'Guest';
      if (result?.action === 'moved') toast(`${memberName} was moved to ${headName}'s Family`);
      else if (result?.action === 'already-member') toast(`${memberName} is already in ${headName}'s Family`);
      else toast(`${memberName} was added to ${headName}'s Family`);
      refresh();
      return result;
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Could not add family member';
      toast(message);
      throw err;
    }
  };

  // setMobile keeps the dedup state honest when the number changes.
  const setMobile = (v) => { setF('MobileNumber', ten(v)); if (checkedMobile) { setDupes([]); setCheckedMobile(''); } };

  // ── Bulk import from the phone book ─────────────────────────────────────────
  const openBulk = async () => {
    if (sideMissing) { toast('Ask your event admin to set your side (Groom or Bride) before adding guests'); return; }
    const preset = functions.find(f => String(f.FunctionId) === String(fnFilter));
    const remembered = readDefaults(event.EventId);
    const rememberedIds = (remembered.FunctionIds || []).map(String).filter(id => allOpenIds.includes(id));
    setBulkSelected(new Set()); setBulkSearch(''); setBulkFunctionIds(newGuestFunctionIds(preset && !isPastFunction(preset) ? [String(fnFilter)] : rememberedIds, functions));
    setBulkWeddingOff(false);
    setBulkSide(lockedSide || (isMarriage && remembered.Side) || guestSide); setBulkVisibility('Private'); setBulkOpen(true);
    setBulkLoading(true);
    try {
      const list = await getDeviceContacts();
      setDeviceContacts(list);
    } catch (e) {
      toast(e.message || 'Could not read contacts'); setBulkOpen(false);
    } finally { setBulkLoading(false); }
  };
  const toggleBulkFunction = (functionId) => {
    const res = toggleWithWedding(bulkFunctionIds, functionId, functions, bulkWeddingOff);
    setBulkWeddingOff(res.weddingOff);
    setBulkFunctionIds(res.ids);
  };
  const toggleBulk = (key) => setBulkSelected(s => {
    const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n;
  });
  // mobile → Set(FunctionId) already in MY list, so the picker can show "Added"
  // and only add the missing functions for a contact.
  const myFnsByMobile = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      if (String(r.CreatedBy) !== String(me) || !r.MobileNumber) return;
      if (!m.has(r.MobileNumber)) m.set(r.MobileNumber, new Set());
      rowFunctionIds(r).forEach(id => m.get(r.MobileNumber).add(String(id)));
    });
    return m;
  }, [rows, me]);
  // Functions (of the chosen ones) this contact still needs; [] = fully added already.
  const missingFnsFor = (c) => {
    const have = c.mobile ? myFnsByMobile.get(c.mobile) : null;
    return bulkFunctionIds.filter(id => !(have && have.has(String(id))));
  };
  const bulkVisible = useMemo(() => {
    const s = bulkSearch.trim().toLowerCase();
    const list = s ? deviceContacts.filter(c => `${c.display} ${c.mobile}`.toLowerCase().includes(s)) : deviceContacts;
    return list.map(c => ({ ...c, added: bulkFunctionIds.length > 0 && missingFnsFor(c).length === 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceContacts, bulkSearch, bulkFunctionIds, myFnsByMobile]);
  const bulkSelectable = bulkVisible.filter(c => !c.added);
  const bulkAllVisibleSelected = bulkSelectable.length > 0 && bulkSelectable.every(c => bulkSelected.has(c.key));
  const toggleBulkAll = () => setBulkSelected(s => {
    const n = new Set(s);
    if (bulkAllVisibleSelected) bulkSelectable.forEach(c => n.delete(c.key));
    else bulkSelectable.forEach(c => n.add(c.key));
    return n;
  });

  const saveBulk = async () => {
    if (!bulkFunctionIds.length) return toast('Pick at least one function');
    const chosen = deviceContacts.filter(c => bulkSelected.has(c.key));
    if (!chosen.length) return toast('Select at least one contact');
    setBulkSaving(true);
    setBulkProgress({ done: 0, total: chosen.length });
    let people = 0, invites = 0, skipped = 0;
    try {
      const addedByName = newOwner.name || '';
      // ONE API call per contact with only the functions they are not yet in
      // (the API inserts one Evt_Guests row per function).
      for (const c of chosen) {
        const fnIds = missingFnsFor(c);
        if (!fnIds.length) { skipped++; setBulkProgress(p => ({ ...p, done: p.done + 1 })); continue; }
        await AddGuestToFunctions({
          EventId: event.EventId, OrganiserId: organiser.OrganiserId, FunctionIds: fnIds,
          Surname: c.surname || null, GuestName: c.name || c.display || null, FatherHusband: c.fatherHusband || null,
          MobileNumber: c.mobile || null, Side: isMarriage ? (lockedSide || bulkSide) : 'Common', Visibility: bulkVisibility, ExpectedCount: 1,
          InviteStatus: 'Pending', AddedByName: addedByName || null, AddedByMobile: newOwner.mobile,
          Valid: 1, CreatedBy: newOwner.id, UpdatedBy: me,
        });
        people++; invites += fnIds.length;
        setBulkProgress(p => ({ ...p, done: p.done + 1 }));
      }
      writeDefaults(event.EventId, { FunctionIds: bulkFunctionIds, Side: bulkSide, Visibility: bulkVisibility });
      toast(`Added ${people} guest${people === 1 ? '' : 's'} (${invites} invite${invites === 1 ? '' : 's'})${skipped ? ` · ${skipped} already in your list` : ''}`);
      setBulkOpen(false); refresh();
    } catch {
      toast(`Added ${people}, then a save failed — check your connection and try again`);
      refresh();
    } finally { setBulkSaving(false); }
  };

  // "Find person" in the add-guest form: picking someone known (a guest of any
  // event of this organiser, or a synced phone contact) fills the head fields.
  const people = usePeopleSearch({
    organiserId: organiser.OrganiserId, userId: me, allGuests: isAdmin,
    onPick: (p) => fillHead(p),
  });

  // Picking a person fills the head (or a family member's row) in one go.
  function fillHead(p) {
    setForm(f => ({
      ...f, Surname: nameInput(p.Surname || ''), GuestName: nameInput(p.GuestName || ''),
      FatherHusband: normaliseText(p.FatherHusband || ''), MobileNumber: ten(p.MobileNumber || ''),
    }));
    if (p.MobileNumber) checkMobile(ten(p.MobileNumber));
  }
  // Type-ahead from MY synced phone book in Surname / Name / Father / Mobile
  // (target 'head' = the guest or family head, a number = that family member).
  const contactSuggest = useContactSuggest({
    organiserId: organiser.OrganiserId, userId: me,
    onPick: (p, target) => {
      if (target === 'head' || target == null) { fillHead(p); return; }
      setParty(list => list.map((m, i) => (i === target ? {
        ...m, Surname: nameInput(p.Surname || ''), GuestName: nameInput(p.GuestName || ''), MobileNumber: ten(p.MobileNumber || ''),
      } : m)));
    },
  });

  // Fast grid entry of many guests (see useQuickAdd).
  const quick = useQuickAdd({
    event, organiser, user, functions, rows, isMarriage, lockedSide, defaultSide, sideMissing,
    fnFilter, refresh, toast, readDefaults, writeDefaults, rowFunctionIds, newOwner,
  });

  const countGroup = useCountGroup({
    event, organiser, user, functions, team, isMarriage, refresh, toast, rowFunctionIds,
  });

  return {
    countGroup, isCountGroup, quick, people, contactSuggest, listView, setListView, familyGroups, individualRows, businessRows, viewCounts, openFamilyFor, familyHeadNote, sameNameGuests, nameTwins, openTwin, mergePick, toggleMergePick, mergeDuplicates, merging, familyHeadExisting, familyMemberExisting, existingFamilyMembers, setExistingMemberField, commitExistingMember, removeExistingMember, savingExisting, toggleRowFunction, rowHasFunction, savingFn, isPublic, setVisibilityFor, savingVis, setGroupForRow, setRelationFor, moveFamilyMember, movingMember, isPicked, togglePick, pickRows, clearPicked, pickedFrom, addedByTargets, setAddedByFor, setEditingAddedBy, savingOwner, setGroupFor, savingGroup, setGuestTypeFor, visFilter, setVisFilter, visCounts, typeCounts, cardInfo, existingNotice, deliveryNames, cardHeadOptions,
    isAdmin, canView, canAdd, canEdit, canDelete, mine, isMarriage, sideOptions, lockedSide, sideMissing, newOwner,
    functions, invitableFunctions, pickerFunctions, rows, loading, q, setQ, fnFilter, setFnFilter, fnCount, fnCountText, fnMode, setFnMode, fnFilterLabel, sideFilter, setSideFilter, sideCounts, cardFilter, setCardFilter, cardCounts, fnName,
    filtered, stats, adderCount, distinctPeople, sort, setSort, toggleSort, SORT_OPTIONS, report, verifyReport,
    adderOptions, adderFilter, setAdderFilter, adder, adderIsMe: String(adderFilter) === String(me), groupOptions, groupNames, groupFilter, setGroupFilter,
    singleFunction, partyLine,
    functionSets, setsOff, toggleSet, setsAllOn, toggleAllSets, chosenSets, setsReport,
    familyCards, visibleCards, guestTypeFilter, setGuestTypeFilter, isBusiness, actionCooldown, beginFamilyAction, callGuest,
    open, setOpen, editing, form, setF, setMobile, selectedFunctionIds, toggleFunction,
    party, addParty, setPartyField, removeParty, togglePartyFunction, partyFunctionIds, familyMode,
    addMode, setAddMode, dupes, checkedMobile, blockedSameFn, hasOtherInvites, needsCheck,
    openAdd, openEdit, clearForm, isDirty, canImport, importContact, importPartyContact,
    numberChoice, chooseContactNumber, cancelNumberChoice: () => setNumberChoice(null),
    knownSurnames, knownNames, nameMatches, clashingFunctions, familyOptions,
    pickExisting, save, saving, mark, del, me,
    allFunctionsSelected, toggleAllFunctions, addedThisSession,
    inviteRow, inviteSharing, inviteCooldown, openInvite, closeInvite, sendInvite, guestHasAllFunctions,
    addOrMoveFamilyMember,
    // bulk import
    canBulkImport, bulkOpen, setBulkOpen, bulkLoading, bulkSaving, bulkProgress, deviceContacts, bulkVisible, bulkSelectable,
    bulkSelected, toggleBulk, bulkSearch, setBulkSearch, bulkAllVisibleSelected, toggleBulkAll,
    bulkFunctionIds, setBulkFunctionIds, toggleBulkFunction, bulkSide, setBulkSide, bulkVisibility, setBulkVisibility,
    openBulk, saveBulk,
  };
}
